/*
 * VNCtalk - an enterprise real-time communication solution including chat, video and audio conferencing, screen sharing, voice messaging, file sharing, broadcasts, document collaboration and much more.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, Renderer2,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { DomSanitizer, SafeHtml, SafeUrl } from "@angular/platform-browser";
import { Broadcaster, Events } from "../../providers";
import format from "date-fns/format";
import { formatDistanceToNow, isBefore } from "date-fns";
import enUS from "date-fns/locale/en-US";
import de from "date-fns/locale/de";
import fr from "date-fns/locale/de";
import { VNCTalkNotificationsService } from "../../../notifications";
import { ConfigService } from "../../../../config.service";
import { Subject, combineLatest, distinctUntilChanged } from "rxjs";
import { CommonService } from "../../../../shared/providers/common.service";
import { TranslateService } from "@ngx-translate/core";
import { CommonUtil } from "../../../utils/common.util";
import { getUserJID, getActiveTab, getAppSettings, getBaseApiUrl, getActiveFilter, getIsAppOnline } from "../../../../reducers/index";
import { getFileInProgressByMessageId, getIsSideRostersExpanded, getMessageById, TalkRootState } from "../../../reducers/index";
import { Store } from "@ngrx/store";
import { JID } from "../../../models/jid.model";
import { Message, MessageStatus, OriginalMessage } from "../../../models/message.model";
import { ContactRepository } from "../../../repositories/contact.repository";
import { ConversationRepository } from "../../../repositories/conversation.repository";
import * as textile from "textile-js";
import { Observable } from "rxjs";
import * as filesize from "filesize";

import { MessageService } from "../../../../shared/providers/message.service";
import { environment } from "../../../../environments/environment";
import { AppService } from "../../../../shared/services/app.service";
import { ToastService } from "../../../../shared/services/toast.service";
import { ConferenceRepository } from "../../../repositories/conference.repository";
import { NavigationEnd, Router } from "../../../../../../node_modules/@angular/router";
import { Conversation } from "../../../models/conversation.model";
import { SearchRepository } from "../../../../repositories/search.repository";
import { isNullOrUndefined } from "util";
import { MessageUtil } from "../../../utils/message.util";
import { MatMenuTrigger } from "@angular/material/menu";
import { BroadcastKeys, ConstantsUtil } from "../../../utils/constants.util";
import { DatabaseService } from "../../../services/db/database.service";
import { ElectronService } from "app/shared/providers/electron.service";
import { SelectProfile, SetActiveFilter, SetActiveTab } from "app/actions/app";
import { Contact } from "app/talk/models/contact.model";
import { MessageUpdateAction, MultiConversationUpdateLastMessage } from "app/talk/actions/message";
import { FilesStorageService } from "app/talk/services/files-storage.service";
import { DatetimeService } from "app/talk/services/datetime.service";
import { DateUtil } from "app/talk/utils/date.util";
import { MatDialog } from "@angular/material/dialog";
import { MatDatepicker } from "@angular/material/datepicker";
import { AvatarRepository } from "app/talk/repositories/avatar.repository";
import { DocumentPreviewComponent, VncLibraryService } from "vnc-library";
import {
  TOPIC_MENTION_MESSAGE_RECEIVER,
} from "../../../../channels/comments/comments.component";
import {GetFullNamePipe} from "../../pipes";
import { distinctUntilKeyChanged, filter, map, take, takeUntil } from "rxjs/operators";
import { BehaviorSubject } from "rxjs";
import { ChannelRepository } from "../../../../channels/repository/channel.repository";
import { SmartLinkData, SmartLinkService } from "../../../../channels/smart-link.service";
import { SmartLinkSearchResultType } from "../../../../channels/smart-link-popup/smart-link-popup.component";
import { Channel } from "../../../../channels/models/channel.model";
import { ChannelService } from "../../../../channels/channel.service";
import { FileActionsService } from "../../../services/file-actions.service";
import { File } from "../../../../channels/models/file.model";
import {SocialRepository} from "../../../repositories/social.repository";
import { AppSettings } from "app/talk/models/app-settings.model";
import { ConversationContentUpdate, ConversationUpdate, DownloadFileInProgress, SetMessageToCopyAndDelete } from "app/talk/actions/conversation";
import { NotificationService } from "app/talk/services/notification.service";
import { LoggerService } from "app/shared/services/logger.service";
import { FilePreviewDialogComponent } from "../dialogs/file-preview-dialog/file-preview-dialog.component";
import { EmojiReactionService } from "app/talk/services/emoji-reaction.service";
import { HttpEventType } from "@angular/common/http";
import { saveAs } from "file-saver";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { SetMediaSharingStatus } from "app/actions/media";
import { SharingService } from "app/talk/services/sharing.service";
import { ConversationService } from "app/talk/services/conversation.service";
import { ExpandChatRightSidebar, ExpandChatRightSidebarWithProfile, ExpandSideRostersList } from "app/talk/actions/layout";
import { XmppService } from "app/talk/services/xmpp.service";
import { VNCCommonDialogComponent } from "app/shared/components/common-dialog/common-dialog.component";

import vCard from "vcard-parser";
import { RedmineApiService } from "app/talk/services/redmine-api.service";
import { FullViewImageComponent } from "../dialogs/full-view-image/full-view-image.component";

const TOTAL_VIDEO_DETECT_TIME = 10;
const ATTACHMENT_IMAGE_MAX_HEIGHT_PORTRAIT = 200;
const ATTACHMENT_VIDEO_MAX_HEIGHT_PORTRAIT = 268;

const SHOW_MESSAGE_HEIGHT_DEBUG_INFO = false;

export const TICKET_MENTION = "ticket_mention";
export const META_TASK_MENTION = "meta_task_mention";

interface IReactionEmoji{
  code:string,
  symbol:string
}

@Component({
  selector: "vp-message",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./message.html"
})

export class MessageComponent implements OnDestroy, OnInit, AfterViewInit, OnChanges {
  @Input() message: Message | any;
  @Input() messageHeight: number;
  @Input() mIndex: Message | any;
  @Input() messageStatus: MessageStatus | any;
  @Input() messageTimestamp: number;
  @Input() messageStarred: boolean;
  messageUrlPreviewData: any;
  @Input() messageRedminePreview: any;
  @Input() uploadFileInProgress: any;
  @Input() isAdvancedSearch: boolean;
  @Input() hideKebab: boolean;
  @Input() isMiniChat: boolean;
  @Input() messageAttachment: any;
  @Input() isDifferentSenderThanLastMessage: any;
  encryptionMessage: string = "";
  callHistory = "";

  @Input() background: any;
  @Input() chatType: any;
  @Input() keyword: any;
  @Input() showEditMenu = false;
  @Input() isLastMessage = false;
  @Input() inSelectionMode = false;
  @Input() isSearchResult = false;
  @Input() isStarred: boolean;
  @Input() allContacts: any;

  @Output() onEdit = new EventEmitter();
  @Output() onStarChange = new EventEmitter();
  @Output() onDelete = new EventEmitter();
  @ViewChild("prioritySelect") prioritySelect;
  @ViewChild("projectSelect") projectSelect;
  @ViewChild("projectVersionSelect") projectVersionSelect;
  @ViewChild("assigneeSelect") assigneeSelect;
  @ViewChild("statusSelect") statusSelect;
  @ViewChild("reactionMainMenu") reactionMainMenu;
  lang = "en";
  locale: any;
  messageBody: SafeHtml = "";
  destroy$ = new Subject();
  private static SVG_TYPE = "svg";
  private static GIF_TYPE = "gif";
  isAudio = false;
  isSupportedVideo = false;
  isVCFfile = false;
  isAlreadyExistingContact = false;
  isImage = false;
  showMenu = false;
  mapUrl: SafeUrl = "";
  attachmentUrl: SafeUrl = "";
  imagePreviewUrl: SafeUrl = "";
  isUrlPreview = false;
  isOTAFormDisplayed = false;
  oat = 0;
  fromJid: string;
  displayTime: string = "";
  originalMessage: any = "";
  repliedMessage: SafeHtml = "";
  repliedAudioUrl: string = "";
  showPlayer = "none";
  isVideoLoading = false;
  amIAudience = false;
  localFilePath = "";
  invalidPreviewUrlLogo: boolean = false;
  private onlyText: boolean = true;
  private userJID: JID;
  private isAlive$ = new Subject<boolean>();
  private previewClosed$ = new Subject<boolean>();
  @ViewChild("mainMenu", {static: false}) mainMenu: MatMenuTrigger;
  @ViewChild("reactMenu", {static: false}) reactMenu: MatMenuTrigger;
  @ViewChild("videoPlayer", {static: false}) videoPlayer: ElementRef;
  @ViewChild(MatDatepicker) dueDatePicker: MatDatepicker<Date>;
  minDate = new Date();
  isMainMenuOpen = false;
  isMyMessage: boolean;
  percentUploaded: string = "0%";
  uploadedPercent = 0;
  recodedPercent = 0;
  localUrl;
  isLoading = false;
  isIOS = false;
  isMeeting: boolean = false;
  companyName: string;
  isExternalUser: boolean;
  activeTab = "chat";
  isOnElectron = environment.isElectron;
  errorMessage: string;
  checkPlayable: any;
  displayPlayer: boolean;
  videoPreviewUrl = "";
  isRecoding = false;
  disableEmailToFriend: boolean = environment.disableEmailToFriend;
  disableAddTask: boolean = environment.disableAddTask;
  EMAIL_TO_A_FRIEND_KEY = environment.theme === "hin" ? "EMAIL_TO_A_FRIEND_HIN" : "EMAIL_TO_A_FRIEND";
  FILE_SENDING_FAILED_KEY = environment.theme === "hin" ? "FILE_SENDING_FAILED_HIN" : "FILE_SENDING_FAILED";
  translatedUrl: string = "";
  isGroup: boolean;
  videoHeight: number;
  videoWidth: number;
  hasMention: boolean;
  forwardTime: string;
  isOlderThan5Minutes: boolean = false;

  // redmine
  isRedmineUrl = false;
  issueId: any;
  issue: any;
  dueDate: any;
  priorityOptions: any[] = [];
  isETAFormDisplayed: boolean;
  isCommentFormDisplayed: boolean;
  commentText: string;
  mentionableUsers = [];
  isGettingRemineTicketEditableRights = false;
  isUpdatingTicket = false;

  noAnswer: any;
  showDefaultAvatar: boolean;
  totalParticipants: number;
  translationKey: string;
  translationParam: { groupName: any; };
  fullUrl: any;
  hideInsecureContent: boolean;
  isInsecureContent: boolean;
  missCallJids: any;
  isMobileSize = CommonUtil.isMobileSize();
  isIPadSize = CommonUtil.isIPadSize();
  addTimestamp: boolean;
  fullActorName: string;
  actorName: string;
  replyToText: string = "Replying to";

  isHin = environment.theme === "hin";
  isDocument: boolean;
  triggerGallary = new Subject<any>();
  fontSize: string;
  enabledOwnCloud = !!localStorage.getItem("enabledOwnCloud") && localStorage.getItem("enabledOwnCloud") === "true";

  channelEntityPreviewType$ = new BehaviorSubject<string>("");
  channelEntityPreviewData$ = new BehaviorSubject<any>(null);

  commentActionType = CommentAction;
  currentFilter: string;
  activeFilter: any;
  emojiOnly: boolean;
  urlsCount: any;
  settings: AppSettings = {};
  messageRedmineVersionPreview: any;
  isVersionUrl: boolean;
  previewDialog: any;
  isDifferentSender$ = new BehaviorSubject<boolean>(false);
  showAvatar: boolean = false;
  isAppOnline: boolean;
  previewNotAvailableOffline: boolean;
  callEnded: string;

  outgoingActionEvent = new Subject<string>();
  favoriteIsStarred: any;
  headerActions: any;
  translations: any = {};

  emojiCollection: IReactionEmoji[] = [
    {code:":+1:", symbol:"👍"},
    {code:":-1:", symbol:"👎"},
    {code:":heart:", symbol:"❤️"},
    {code:":grinning:", symbol:"😀"},
    {code:":hushed:", symbol:"😯"},
    {code:":slightly_frowning_face:", symbol:"🙁"},
    {code:":angry:", symbol:"😠"},
    {code:":heavy_check_mark:", symbol:"✔️"},
  ];
  isPreviewLoading: boolean;
  fileUploaded: boolean;
  viewGridMode: boolean = false;
  formatToday: any;
  messageDate: any;
  formatTimestamp: boolean;

  unlistenScrollEvents: any;
  unlistenArrowEvents: any;
  selectedConversation: unknown;
  botJid: any;
  previewNotAvailableText: any;
  isMobileScreen: boolean = false;
  previewOnly: boolean;
  observer: any;
  displayed: boolean;
  processURL$: any;
  cachedMessage: string;
  checkUploadProgress$: any;
  loadingUrl: string;
  get isMobileDevice():boolean{
    return environment.isCordova || CommonUtil.isOnNativeMobileDevice();
  }

  fullviewImageMobile: any;

  constructor(private sanitizer: DomSanitizer,
              private el: ElementRef,
              private vncLibraryService: VncLibraryService,
              private avatarRepo: AvatarRepository,
              private searchRepo: SearchRepository,
              private notificationsService: VNCTalkNotificationsService,
              private changeDetectorRef: ChangeDetectorRef,
              private dialog: MatDialog,
              private commonService: CommonService,
              private appService: AppService,
              private translate: TranslateService,
              private configService: ConfigService,
              public conferenceRepo: ConferenceRepository,
              private toastService: ToastService,
              private contactRepo: ContactRepository,
              private convRepo: ConversationRepository,
              private messageService: MessageService,
              private broadcaster: Broadcaster,
              private breakpointObserver: BreakpointObserver,
              private router: Router,
              private databaseService: DatabaseService,
              private electronService: ElectronService,
              private conversationRepo: ConversationRepository,
              private filesStorageService: FilesStorageService,
              private datetimeService: DatetimeService,
              private notificationService: NotificationService,
              private getFullName: GetFullNamePipe,
              private store: Store<TalkRootState>,
              private sharingService: SharingService,
              private channelRepository: ChannelRepository,
              private socialRepository: SocialRepository,
              private matDialog: MatDialog,
              private channelService: ChannelService,
              private _smartLinkService: SmartLinkService,
              private logger: LoggerService,
              private emojiReactionService: EmojiReactionService,
              private fileActionsService: FileActionsService,
              private convService: ConversationService,
              private xmpp: XmppService,
              private redmineService: RedmineApiService,
              private renderer: Renderer2) {
              this.enabledOwnCloud = this.configService.get("enabledOwnCloud");
    // this.logger.info("[MessageComponent][constructor]", textile);
    this.botJid = this.configService.get("botJid");
    this.lang = CommonUtil.getDefaultLang();
    this.isMobileScreen = this.breakpointObserver.isMatched("(max-width: 767px)");
    this.loadingUrl = CommonUtil.getFullUrl("/assets/loading-image.gif");

    this.conversationRepo.resetRedminePreview$.pipe(takeUntil(this.isAlive$)).subscribe((rm) => {
      if (rm && rm.id) {
        if (rm.id === this.messageRedminePreview.id && !this.previewOnly) {
          this.messageData();
          setTimeout(() => {
            this.changeDetectorRef.markForCheck();
          }, 500);
        }
      }
    });

  }

  ngOnInit() {
    this.processMessage();
    this.store.select(getIsAppOnline).pipe(takeUntil(this.isAlive$)).subscribe(v => {
      if (!this.isAppOnline && !!v && !this.previewOnly) {
        this.isAppOnline = v;
        this.previewNotAvailableOffline = false;
        this.processMessageUrl();
      }
      this.isAppOnline = v;
    });
    this.logger.info("messageUrlPreviewData", this.messageUrlPreviewData, this.message, this.message.id);
  }

  handleEvents() {

    this.store.select(getAppSettings)
    .pipe(distinctUntilKeyChanged("selectedFontSize")
      , takeUntil(this.isAlive$))
      .subscribe(options => {
        this.fontSize = !!options.selectedFontSize ? `font-${options.selectedFontSize}` : "font-16";
      });

    this.store.select(getUserJID).pipe(filter(jid => !!jid), take(1)).subscribe(jid => {
      this.userJID = jid;
      if (this.message && this.fromJid) {
        this.isMyMessage = this._isMyMessage();
        this.changeDetectorRef.markForCheck();
      }
    });

    this.appService.getAppSettings().pipe(distinctUntilKeyChanged("hideInsecureContent"), takeUntil(this.isAlive$)).subscribe(options => {
      // this.logger.info("[hideInsecureContent]", options);
      if (typeof options.hideInsecureContent === "undefined") {
        this.hideInsecureContent = true;
      } else {
        this.hideInsecureContent = !!options.hideInsecureContent;
      }
      if (this.message) {
        this.processImageVideoAudioAttachment();
        this.processMessageUrl();
      }

      this.changeDetectorRef.markForCheck();
    });

    this.appService.getAppSettings().pipe(distinctUntilKeyChanged("addTimestamp"), takeUntil(this.isAlive$)).subscribe(options => {
      // this.logger.info("[addTimestamp]", options);
      let addTimestamp = !!options.addTimestamp;
      if (addTimestamp !== this.addTimestamp) {
        this.addTimestamp = addTimestamp;
        this.setDisplayTime();
        this.changeDetectorRef.markForCheck();
      }

    });

    this.commonService.currentLanguage.pipe(takeUntil(this.isAlive$)).subscribe((lang: string) => {
      this.lang = lang;
      this.setDisplayTime();
      this.setCallHistory();
      this.changeDetectorRef.markForCheck();
      if (this.lang === "de") {
        this.locale = de;
      }
      if (this.lang === "fr") {
        this.locale = fr;
      }
      if (this.lang === "en") {
        this.locale = enUS;
      }
    });

    this.store.select(getActiveTab).pipe(takeUntil(this.isAlive$)).subscribe(tabName => {
      this.activeTab = tabName;
    });
    this.store.select(getActiveFilter).pipe(takeUntil(this.isAlive$)).subscribe(name => {
      this.activeFilter = name;
    });

    this.broadcaster
      .on(BroadcastKeys.DETECT_CHANGES_IN_CHAT_WINDOW)
      .pipe(takeUntil(this.isAlive$))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });

      this.breakpointObserver.observe(["(max-width: 767px)"]).pipe(takeUntil(this.isAlive$)).subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.isMobileScreen = true;
        } else {
          this.isMobileScreen = false;
        }
        this.changeDetectorRef.markForCheck();
      });
    this.broadcaster.on<any>("ticketPreviewUpdate")
    .pipe(takeUntil(this.isAlive$))
    .subscribe(data => {
      if (data == this.message.id && !this.previewOnly) {


        this.messageData();
      }
    });


    this.conversationRepo.getSelectedConversation().pipe(takeUntil(this.isAlive$)).subscribe(selectedConversation => {
      this.selectedConversation = selectedConversation;
      this.changeDetectorRef.markForCheck();
    });

    this.conversationRepo.websocketData$.pipe(takeUntil(this.isAlive$)).subscribe(data => {
      this.updateMessageContent(data);
    });

    this.store.select(getAppSettings).pipe(takeUntil(this.isAlive$)).subscribe(options => {
      const isTimeSettingsUpdated = this.settings.timeFormat !== options.timeFormat
      || this.settings.timezone !== options.timezone || this.settings.dateFormat !== options.dateFormat;
      this.settings = options;
      if (isTimeSettingsUpdated) {
        this.setDisplayTime();
      }
    });
    this.broadcaster.on<any>("fileSent").pipe(takeUntil(this.isAlive$)).subscribe((msg) => {
      if (this.message.id === msg.id) {
        this.fileUploaded = true;
        this.message.attachment = msg.attachment;
        this.message.body = this.message.attachment.url;
        this.message.cachedContent = this.message.attachment.url;
        this.logger.info("[messageComponent] fileUploaded ", this.message, msg);
        const tempUrl = (this.message.attachment.url.indexOf("?") > -1) ? this.message.attachment.url.split("?")[0] : this.message.attachment.url;
        this.isSupportedVideo = CommonUtil.isSupportedVideo(this.message.attachment.fileType);
        if (this.isSupportedVideo && this.message.attachment.url.includes("/share.php/")) {
          this.videoPreviewUrl = this.messageService.getAttachmentPreviewUrl(tempUrl);
          this.isRecoding = true;
        }
        this.changeDetectorRef.markForCheck();
      }
    });
    this.broadcaster.on<any>("fileRecoded").pipe(takeUntil(this.isAlive$)).subscribe((msg) => {
      if (this.message.id === msg.id) {
        this.fileUploaded = true;
        this.message.attachment = msg.attachment;
        this.logger.info("[messageComponent] fileRecoded ", this.message, msg);
        const tempUrl = (this.message.attachment.url.indexOf("?") > -1) ? this.message.attachment.url.split("?")[0] : this.message.attachment.url;
        this.isSupportedVideo = CommonUtil.isSupportedVideo(this.message.attachment.fileType);
        if (this.isSupportedVideo && this.message.attachment.url.includes("/share.php/")) {
          this.videoPreviewUrl = this.messageService.getAttachmentPreviewUrl(tempUrl);
          this.isRecoding = false;
        }
        this.changeDetectorRef.markForCheck();
      }
    });
    this.broadcaster.on<string[]>("updateDeletedStatus").pipe(takeUntil(this.isAlive$)).subscribe((ids) => {
      // this.logger.info("updateDeletedStatus", ids);
      if (ids.includes(this.message.id)) {
        this.message.body = null;
        this.message.isDeleted = true;
        this.changeDetectorRef.markForCheck();
      }
    });
    this.broadcaster.on(Events.DOCUMENT_CLICKED).pipe(takeUntil(this.isAlive$)).subscribe(() => {
      if (this.isMainMenuOpen) {
        this.mainMenu && this.mainMenu.closeMenu();
      }
    });

    this.broadcaster.on("closeDocumentPreview").pipe(takeUntil(this.isAlive$)).subscribe(() => {
      this.closePreviewDialog();
    });

    if (this.mainMenu) {
      this.mainMenu.menuOpened.pipe(takeUntil(this.isAlive$)).subscribe(() => {
        setTimeout(() => {
          this.isMainMenuOpen = true;
        }, 100);
      });

      this.mainMenu.menuClosed.pipe(takeUntil(this.isAlive$)).subscribe(() => {
        this.isMainMenuOpen = false;
      });
    }
    this.broadcaster.on("selectMessage").pipe(takeUntil(this.isAlive$)).subscribe(msgId => {
      if (msgId === this.message.id) {
        this.processMessageBody();
        this.logger.info("[MessageComponentUpdate] selectMessage ", this.message, this.cachedMessage);
        this.convRepo.contentForClipBoard[this.message.id] = this.cachedMessage;
      }
    });
    this.store.select(state => getMessageById(state, this.message.id))
    .pipe(filter(v => !!v), distinctUntilChanged(), takeUntil(this.isAlive$))
    .subscribe(message => {
      // this.logger.info("[handleEvents]getMessageById", this.message.id, message);

      this.message = message;
      if (!!message.attachment && !!message.attachment.recodingRequired && !!message.attachment.recodingProgress) {
        this.recodedPercent = message.attachment.recodingProgress;
        if (this.recodedPercent === 100) {
          this.isRecoding = false;
        }
        this.changeDetectorRef.markForCheck();
        // this.logger.info("[MessageComponentUpdate] recodingstat ", this.isRecoding, this.recodedPercent);
      }
    });
    this.conversationRepo.getConversationById(this.message.convTarget).pipe(distinctUntilChanged(), takeUntil(this.isAlive$)).subscribe(conv => {
      if (!!conv) {
        this.amIAudience = conv.audience_only;
        this.changeDetectorRef.markForCheck();
      }
    });
    this.broadcaster.on<any>("UPDATE_ROLE_IN_CURRENTCHAT").pipe(takeUntil(this.isAlive$)).subscribe((v: any) => {
      if (this.message.convTarget === v) {
        setTimeout(() => {
          this.conversationRepo.getConversationById(v).pipe(take(1)).subscribe(conv => {
              this.amIAudience = conv?.audience_only;
              this.changeDetectorRef.markForCheck();
            });
        }, 200);
      }
    });
  }

  regenerate() {
    this.conversationRepo.WS.send(
      JSON.stringify({
        prompt,
        target: this.userJID.bare,
        options: { max_generated_tokens: 500 }
      })
    );
  }

  updateMessageContent(message: string) {
    try {
      const data = JSON.parse(message);
      this.logger.info("updateMessageContent", data);
      if (!this.message.isDeleted && data.id === this.message.id) {
        this.logger.info(data);
        if (data.status === "finished") {
          if (data.attachment) {
            this.message.attachment = data.attachment;
            this.processImageVideoAudioAttachment();
          }
          this.messageStatus = MessageStatus.SENT;
        }
        this.message.body = data.response;
        this.messageBody = this.sanitizer.bypassSecurityTrustHtml(data.response.replace(/<a href=/g, "<a target=\"_blank\" class=\"open-new-window\" href=")
        .replace(/<a class="([a-z\s0-9]*)"\shref=/g, "<a target=\"_blank\" class=\"open-new-window\" href="));
        this.changeDetectorRef.markForCheck();
      }
    } catch (error) {

    }
  }

  checkTranslationParam() {
    let text;
    if (this.message && (this.message?.group_action === "created" || this.message?.group_action?.type === "created")) {
      let messageBody = this.message?.body || "";
      text = { groupName: messageBody.replace(/Group\s"/, "").replace(/"\sCreated/, "")};
    }
    return text;
  }

  ngAfterViewInit() {

    if (CommonUtil.isMobileSize() || CommonUtil.isIPadSize()) {
      this.closeReactionMenuByScroll();
    }
    const threshold = 0.5; // how much % of the element is in view
    this.observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
              // this.logger.info("IntersectionObserver", this.message.id, entry.isIntersecting);
              if (this.displayed !== entry.isIntersecting) {
                this.displayed = entry.isIntersecting;
                this.isAlive$.next(entry.isIntersecting);
                if (entry.isIntersecting && !this.previewOnly) {
                  this.isAlive$.next(false); // unsubscribe the old
                  this.isAlive$.complete();

                  this.isAlive$ = new Subject<boolean>();
                  this.handleEvents();
                } else {
                  if (this.processURL$) {
                    clearTimeout(this.processURL$);
                  }
                }
              }
            });
        },
        { threshold }
    );
    this.observer.observe(this.el.nativeElement);
    const mentionedUsers = this.el.nativeElement.querySelectorAll(".mentioned-user");

    // Add click event listener to each mentioned user span
    mentionedUsers.forEach((user: HTMLElement) => {
      this.renderer.listen(user, "click", (event) => {
        const jid = user.getAttribute("data-jid");
        this.showChatInformation(event, jid);
      });
    });
    this.shouldApplyClassOnResize();
  }

  closeReactionMenuByScroll(): void {
      this.reactionMainMenu?.menuOpened.subscribe(() => {
        this.unlistenScrollEvents = this.renderer.listen("document", "mousewheel", () => {
          this.reactionMainMenu && this.reactionMainMenu.closeMenu();
        });
        this.unlistenArrowEvents = this.renderer.listen("document", "keydown", (event) => {
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            this.reactionMainMenu && this.reactionMainMenu.closeMenu();
          }
        });
      });

      this.reactionMainMenu?.menuClosed.subscribe(() => {
        if (this.unlistenArrowEvents) {this.unlistenArrowEvents();}
        if (this.unlistenScrollEvents) {this.unlistenScrollEvents();}
      });
  }

  private uploadProgress() {
    if (this.message.uploadFileInProgress) {
      if (this.checkUploadProgress$) {
        this.checkUploadProgress$.unsubscribe();
      }

      this.checkUploadProgress$ = this.store.select(state => getFileInProgressByMessageId(state, this.message.id))
      .pipe(filter(res => !!res)).subscribe((progress: any) => {
        // this.logger.info("[MesasgeComponent][getFileInProgressByMessageId]", this.message.id, progress);
        if (isNaN(progress.loaded) || isNaN(progress.total)) {
          this.percentUploaded = "100%";
          this.uploadedPercent = 100;
        } else {
          this.uploadedPercent = Math.round(progress.loaded * 100 / progress.total);
          this.percentUploaded = Math.round(this.uploadedPercent) + "%";
        }
        this.changeDetectorRef.markForCheck();
      });
    } else if (this.checkUploadProgress$) {
        this.checkUploadProgress$.unsubscribe();
    }
  }

  private processMessage() {
    this.isGroup = this.message.type === "groupchat";

    this.fromJid = this.convRepo.getBareFromMessage(this.message);

    // for a case if passed a SearchMessage class object
    if (!this.fromJid) {
      if (this.message.type === "groupchat") {
        if (typeof this.message.from === "string") {
          this.fromJid = this.message.from.includes("/") ? this.message.from.split("/")[1] : this.message.from;
        }
      } else {
        this.fromJid = this.message.from;
      }
    }
    this.isOlderThan5Minutes = ((Date.now() - this.message?.timestamp) > 300000);
    this.setCallHistory();

    this.isExternalUser = this.contactRepo.isExternalUser(this.fromJid);
    if (this.isExternalUser) {
      this.contactRepo.getContactCompany(this.fromJid).pipe(take(1)).subscribe(companyName => {
        this.companyName = companyName;
      });
    }

    this.isMyMessage = this._isMyMessage();

    if (this.isStarred) {
      this.message.isStarred = true;
    } else {
      if (this.isSearchResult && isNullOrUndefined(this.message.isStarred)) {
        this.message.isStarred = MessageUtil.isFavouriteMessage(this.message);
      }
    }

    this.setDisplayTime();
    this.processMessageBody();
    this.processOriginalMessage();
    this.processImageVideoAudioAttachment();
    this.processMessageUrl();
    this.processLocationAttachment();

    this.broadcaster.on(Events.DOCUMENT_CLICKED).pipe(takeUntil(this.isAlive$)).subscribe(() => {
      if (this.isMainMenuOpen) {
        this.mainMenu && this.mainMenu.closeMenu();
      }
    });

    if (this.mainMenu) {
      this.mainMenu.menuOpened.pipe(takeUntil(this.isAlive$)).subscribe(() => {
        setTimeout(() => {
          this.isMainMenuOpen = true;
        }, 100);
      });

      this.mainMenu.menuClosed.pipe(takeUntil(this.isAlive$)).subscribe(() => {
        this.isMainMenuOpen = false;
      });
    }
    this.getSharedContactData();
  }

  private setCallHistory() {
    if (this.message) {
      const callHistory = this.conferenceRepo.callHistory[this.message.id];
      this.renderCallHistory(callHistory);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // this.logger.info("XXXngOnChanges", changes);
    if (changes.inSelectionMode && Object.keys(changes).length === 1) {
      return;
    }
    if (changes.isDifferentSenderThanLastMessage) {
      this.isDifferentSender$.next(changes.isDifferentSenderThanLastMessage.currentValue);
      this.changeDetectorRef.markForCheck();
    }
    if (changes.message && changes.message.currentValue?.uploadFileInProgress) {
      this.uploadProgress();
    }

    if (this.message.expiry && this.message.expiry * 1000 < new Date().getTime()) {
      this.conversationRepo.deleteMessageFromStoreAndDB(this.message);
    }

    this.conversationRepo.getConversationById(this.message.convTarget).pipe(distinctUntilChanged(), takeUntil(this.isAlive$)).subscribe(conv => {
      if (!!conv && this.amIAudience !== conv.audience_only) {
        this.amIAudience = conv.audience_only;
        this.changeDetectorRef.markForCheck();
      }
    });

    if (this.message.originalMessage) {
      delete this.message.attachment;
    }
    // attachment preview updated
    if (changes.messageAttachment && changes.messageAttachment.previousValue
      && changes.messageAttachment.previousValue.url !== changes.messageAttachment.currentValue?.url) {
      this.message.checkedVideo = false;
      this.processImageVideoAudioAttachment();
    }

    // timestamp updated
    if (changes.message && changes.message.currentValue.messageTimestamp
      && changes.message.previousValue.messageTimestamp !== changes.message.currentValue.messageTimestamp) {
        this.setDisplayTime();
        this.setCallHistory();
      }

    // message updated
    if (changes.message && changes.message.previousValue &&
      (changes.message.previousValue.id !== changes.message.currentValue.id || changes.message.previousValue.body?.length !== changes.message.currentValue.body?.length
        || ((changes.message.currentValue.cannotDecrypt === false) && (changes.message.currentValue.body === "Encrypted message")))) {

        // this.logger.info("[MessageComponent][ngOnChanges] [DataSource] message updated " + this.message?.id + ": ", changes.message.previousValue, changes.message.currentValue);


        // this.messageHeightReal = document.getElementById(`message-${this.message.id}`)?.clientHeight;

        // reset state if message change
        this.resetState();

        this.processMessage();

        if (SHOW_MESSAGE_HEIGHT_DEBUG_INFO) {
          setTimeout(() => {
            const clientHeight = document.getElementById("message-" + changes.message.currentValue.id)?.clientHeight;
            const el = document.getElementById("display-time-" + changes.message.currentValue.id);
            if (clientHeight && el) {
              if (!el.innerHTML.endsWith(";")) {
                el.innerHTML = el.innerHTML + " -> (" + clientHeight + ");";
              }
            }
          }, 1);
        }
      }
      this.shouldApplyClassOnResize();
  }

  private resetState() {
    // this.logger.info("[MessageComponent][resetState]", this.message.id);

    this.isUrlPreview = false;
    //
    this.hasMention = false;
    //
    this.messageUrlPreviewData = null;
    this.isRedmineUrl = false;
    this.issueId = null;
    this.issue = null;
    this.dueDate = null;
    this.isETAFormDisplayed = false;
    this.isCommentFormDisplayed = false;
    this.commentText = "";
    this.isGettingRemineTicketEditableRights = false;
    this.isUpdatingTicket = false;
    this.mentionableUsers = [];

    this.missCallJids = null;
    this.fullActorName = null;

    this.channelEntityPreviewType$.next("");
    this.channelEntityPreviewData$.next(null);

    this.emojiOnly = false;

    this.attachmentUrl = "";
    this.translatedUrl = "";
    this.isAudio = false;
    this.isImage = false;
    this.isDocument = false;
    this.isSupportedVideo = false;
    this.imagePreviewUrl = "";
    this.videoPreviewUrl = "";

    this.fullUrl = null;
    this.urlsCount = undefined;
  }

  processLocationAttachment() {
    // build map url
    if (this.message.location) { // from the local or other vncTalk client
      this.logger.info("[MessageComponent][processLocationAttachment]");
      let center = this.message.location.lat + "," + this.message.location.lng;
      this.mapUrl = this.trustURL(
        "https://maps.googleapis.com/maps/api/staticmap?center=" + center
        + "&zoom=13&size=600x300&maptype=roadmap&markers=color:red%7Clabel:"
        + this.message.location.label + "%7C" + center
        + "&key=" + this.configService.GOOGLE_STATIC_MAP_API_KEY);
        this.changeDetectorRef.markForCheck();
    }
  }

  async emailTo(document) {
    const attachments = [{
        iconName: this.getIconName(document),
        filename: document.fileName,
        path: document.url
    }];
    let dialogStyles: any = {
      "width": "517px",
      "maxWidth": "80vw",
      "visibility": "visible"
    };
    if (CommonUtil.isMobileSize()) {
      dialogStyles = {
        "width": "100%",
        "height": "100%",
        "maxWidth": "100%",
        "visibility": "visible"
      };
    }
    const { EmailSelectedMessageComponent } = await import(
      "../email-selected-message/email-selected-message.component");
    this.matDialog.open(EmailSelectedMessageComponent, Object.assign({
      data: {attachments: attachments},
      backdropClass: "vnctalk-form-backdrop",
      panelClass: ["vnctalk-form-panel", "vnctalk-send-mail-to-friend"],
      disableClose: true,
      autoFocus: true
    }, dialogStyles));
  }

  get iconName() {
    return this.getIconName(this.message.attachment);
  }

  fileType:string = "";
  getIconName(attachment) {
    const fileName = attachment?.fileName || "";
    const fileType = (typeof attachment?.fileType === "string") ? attachment?.fileType : "" || "";
    let iconName = "mdi-file-document-box";
    const zipExtensions = ["7z", "zip", "rar", "tar", "gz", "jar", "tgz"];
    const markUp = ["css", "html", "php", "c", "cpp", "h", "hpp", "js"];
    if (CommonUtil.isAudio(fileType)) {
      iconName = "mdi-file-music";
    } else if (CommonUtil.isVideo(fileType)) {
        iconName = "mdi-video";
    } else if (CommonUtil.isImage(fileType)) {
        iconName = "mdi-image";
    } else if (fileName.toLowerCase().indexOf(".pdf") !== -1) {
        this.fileType = "pdf";
        iconName = "mdi-file-pdf";
    } else if (fileName.toLowerCase().indexOf(".ppt") !== -1 || fileName.toLowerCase().indexOf(".pptx") !== -1) {
        this.fileType = "ppt";
        iconName = "mdi-file-pdf-box";
    } else if (fileName.toLowerCase().indexOf(".doc") !== -1 || fileName.toLowerCase().indexOf(".docx") !== -1) {
        this.fileType = "doc";
        iconName = "mdi-file-word-box";
    } else if (fileName.toLowerCase().indexOf(".xls") !== -1 || fileName.toLowerCase().indexOf(".xlsx") !== -1) {
        this.fileType = "xls";
        iconName = "mdi-file-excel-box";
    } else if (fileName.toLowerCase().indexOf(".csv") !== -1) {
        this.fileType = "csv";
        iconName = "mdi-file-csv-box";
    } else if (zipExtensions.indexOf(fileType.toLowerCase()) !== -1) {
        this.fileType = "zip";
        iconName = "mdi-file-zip-box";
      }  else if (fileName.toLowerCase().indexOf(".pages") !== -1) {
        iconName = "mdi-file-apple-pages-box";
      } else if (fileName.toLowerCase().indexOf(".ai") !== -1) {
        iconName = "mdi-file-adobe-illustrator-box";
      } else if (fileName.toLowerCase().indexOf(".psd") !== -1) {
        iconName = "mdi-file-photshop-box";
      } else if (fileName.toLowerCase().indexOf(".tiff") !== -1) {
        iconName = "mdi-file-tiff-box";
      } else if (fileName.toLowerCase().indexOf(".dxf") !== -1) {
        iconName = "mdi-file-autodesk-box";
      } else if (fileName.toLowerCase().indexOf(".svg") !== -1) {
        iconName = "mdi-file-svg-box";
      } else if (fileName.toLowerCase().indexOf(".txt") !== -1) {
        iconName = "mdi-file-txt-box";
      }  else if (fileName.toLowerCase().indexOf(".xps") !== -1) {
        iconName = "mdi-file-xps-box";
      }  else if (fileName.toLowerCase().indexOf(".ttf") !== -1) {
        iconName = "mdi-file-ttf-box";
      }  else if (fileName.toLowerCase().indexOf(".eps") !== -1) {
        iconName = "mdi-file-eps-box";
      } else if (markUp.indexOf(fileType.toLowerCase()) !== -1) {
        iconName = "mdi-file-markup-box";
      }
    return iconName;
  }

  isForwardOpen: boolean = false;
  async sendTo(document) {
    let message = this.message;
    this.store.select(state => getMessageById(state, document.id)).pipe(take(1)).subscribe(v => message = v);
    const messages = [{
      message: message, row: {
        fileName: document.fileName,
        iconName: this.getIconName(document)
      }
    }];
    let dialogStyles = {
      "width": "517px",
      "maxWidth": "80vw",
      "height": "604px",
      "maxHeight": "604px",
      "visibility": "visible"
    };
    if (CommonUtil.isMobileSize()) {
      dialogStyles = {
        "width": "100%",
        "maxWidth": "100%",
        "height": "100%",
        "maxHeight": "100%",
        "visibility": "visible"
      };
    }
    const { ForwardDialogComponent } = await import(
      "app/talk/shared/components/dialogs/forward/forward.component");
    this.isForwardOpen = true;
    this.dialog.open(ForwardDialogComponent, {
      backdropClass: "forward-dialog-backdrop",
      panelClass: ["forward-dialog-panel", "preview-doc-dialog-panel"],
      disableClose: true,
      autoFocus: false,
      data: { messages: messages, sendTo: true },
      width: dialogStyles.width,
      maxHeight: dialogStyles.maxHeight,
      maxWidth: dialogStyles.maxWidth,
      height: dialogStyles.height
    }).afterClosed().pipe(take(1)).subscribe( (res: any) => {
      this.isForwardOpen = false;
    });

  }

  closePreviewDialog() {
    if (!this.isForwardOpen) {
      this.previewDialog.close();
    }
  }
  downloadMessage(url?: string,document?: any) {
    url = url || this.message.attachment.url;;

    if (!url || url === "") {
        return;
    }

    // if download image on mobile
    if (environment.isCordova &&
        (url.includes(".jpeg") || url.includes(".png") || url.includes(".jpg") || url.includes(".gif"))) {
          const translatedUrl = CommonUtil.translateHINFileURL(url);
          let fileUrl = translatedUrl;
          let headers = [];
          headers.push(
            {
                Key: "Authorization",
                Value: localStorage.getItem("token")
            }
          );
          this.logger.info("[PreviewImageDialogComponent][downloadFile] fileUrl: ", fileUrl);
          this.filesStorageService.downloadAndSaveFileInBackground(fileUrl, headers, CommonUtil.isOnIOS()).subscribe((localFileUrl) => {
            if (CommonUtil.isOnAndroid()) {
              this.translate.get("FILE_DOWNLOADED").pipe(take(1)).subscribe(text => {
                alert(text);
              });
            } else {
              CommonUtil.openLocalFile(localFileUrl);
            }
          }, err => {
            this.logger.error("[MessageComponent][downloadInBackgroundFileAsBlob] err", err);
            this.toastService.show("SOME_UNKNOWN_ERROR");
          });

      this.toastService.show("FILE_STARTED_DOWNLOADING");
        // this.toastService.showSnackbar("FILE_STARTED_DOWNLOADING", 2000);

        // this.convRepo.downloadFileToDownloadsOrGallery(url).subscribe(() => {
        //     this.toastService.showSnackbar("FILE_DOWNLOADED", 2000);
        // }, err => {
        //     this.logger.error("[PreviewImageDialogComponent][downloadFile] error", err);
        // });
    } else if (environment.isElectron) {
        // ElectronService.downloadFile(this.message.attachment.url, "");
        this.electronService.fileDownloader(url).subscribe(() => {
            this.toastService.showSnackbar("FILE_DOWNLOADED", 2000);
        }, (error) => {
            this.logger.info("[PreviewImageDialogComponent][downloadFile] error: ", error);
        });
    } else {
      if (document) {
        let messageDetails = {
          messageId: document.id,
          fileSize: document.fileSizeNumber,
          fileName: document.fileName,
          url: url,
          loaded: 0,
          total: parseInt(this.message.attachment.fileSize)
        };
        MessageUtil.downloadFileFromUrl(url, this.convRepo, this.filesStorageService, this.store, messageDetails);
      }
      else {
        let messageDetails = {
          messageId: this.message.id,
          fileSize: this.message.attachment.fileSize,
          fileName: this.message.attachment.fileName,
          url: url,
          loaded: 0,
          total: parseInt(this.message.attachment.fileSize)
        };
        MessageUtil.downloadFileFromUrl(url, this.convRepo, this.filesStorageService, this.store, messageDetails);
      }
    }
  }

  private _getViewerMode(mediaType: string):string{
    if (mediaType === "pdf"){
      return "pdf";
    } else {
      return "office";
    }
  }

  goToSource(file) {
    this.store.dispatch(new SetActiveTab("chat"));
    this.conversationRepo.navigateToConversation(null);
    setTimeout(() => {
      const highlightCompleteRow: boolean = true;
      this.conversationRepo.navigateToConversationAndJumpToMessage(this.message.convTarget, {id: file.id, timestamp: file.timestamp}, highlightCompleteRow);
      this.previewDialog.close();
    });
  }

    previewDocument(data?: any, allowImage?: boolean) {
      if (this.inSelectionMode) {
        this.selectMessage();
        return;
      }
    this.logger.info("[previewDocument] data", data, this.message);
    this.conversationRepo.turnNativeElectronContextMenuOff();
    let message = this.message;
    if (!!data) {
      message = data;
    }
    let mediaType = message?.attachment?.fileType || "";
    if (mediaType && typeof mediaType !== "string") {
      mediaType = mediaType?.toString();
    }

    this.logger.info("[previewDocument]", this.message?.attachment?.fileType);
    if (CommonUtil.isDocument(this.message?.attachment?.fileType)) {
      if (this.configService.get("disableDocumentPreview") || !CommonUtil.isPreviewAvailableForType(mediaType)) {
        this.toastService.show("UNSUPPORTED_FILE");
        return;
      }
    } else if ([...CommonUtil.unsupportVideoExtensions, ...CommonUtil.zipExtensions].indexOf(mediaType.trim().toLowerCase()) !== -1) {
      this.downloadMessage();
      return;
    }

    const triggerEvent = new Subject<any>();
    triggerEvent.pipe(takeUntil(this.previewClosed$)).subscribe(v => {
      this.handleTriggerEvent(v);
    });
    const translationsKey = ["CLOSE", "MORE_TEXT", "GALLERY_VIEW", "GRID_VIEW", "DELETE", "CREATE_VNCTASK", "EMAIL_TO_A_FRIEND", "ADD_TO_FAVORITES", "REMOVE_FROM_FAVORITES", "GOTO_SOURCE", "COPY_TO_CLIPBOARD", "IMAGE_COPY", "SHARE", "FORWARD", "DOWNLOAD", "COPY_TO_CLIPBOARD", "SEND_TO_OWNCLOUD"];
    let translations: any = {};
    this.translate.get(translationsKey).pipe(take(1)).subscribe(v => {
      translations = v;
      this.translations = v;
    });

    const favoriteAddRemoveText = this.message.isStarred ? translations.REMOVE_FROM_FAVORITES : translations.ADD_TO_FAVORITES;
    this.favoriteIsStarred = this.message.isStarred ? "favorites-filled-new" : "favorites-new";

    this.headerActions = [
      {
        label: favoriteAddRemoveText,
        vncIconName: this.favoriteIsStarred,
        action: "addToFavorites"
      },
      // {
      //   label: translations.SHARE,
      //   vncIconName: 'share-new',
      //   action: 'share'
      // },
      {
        label: translations.FORWARD,
        vncIconName: "forward",
        action: "forward"
      },
      {
        label: translations.DOWNLOAD,
        vncIconName: "download-new",
        action: "download"
      },
      {
        label: translations.GOTO_SOURCE,
        vncIconName: "arrow-subdirectory-right-new",
        action: "gotoSource"
      },
      {
        label: translations.COPY_TO_CLIPBOARD,
        vncIconName: "content-copy",
        action: "copy"
      },
      {
        label: translations.GRID_VIEW,
        vncIconName: "view-tile-grid",
        action: "view"
      }
    ];
    const menuActions = [
      {
        label: translations.EMAIL_TO_A_FRIEND,
        vncIconName: "send-mail-new",
        action: "email"
      },
      {
        label: translations.CREATE_VNCTASK,
        iconName: "mdi-content-copy",
        vncIconName: "task-new",
        action: "task"
      },
      {
        label: translations.DELETE,
        iconName: "mdi-content-copy",
        vncIconName: "delete-new",
        action: "delete"
      }
    ];
    const contextMenuActions = [
      {
        label: translations.DOWNLOAD,
        vncIconName: "download-new",
        action: "download"
      },
      {
        label: this.isImage && !this.isFailed() ? translations.IMAGE_COPY : translations.COPY_TO_CLIPBOARD,
        vncIconName: "content-copy",
        action: "copy-image"
      },
      {
        label: translations.FORWARD,
        vncIconName: "arrow-forward",
        action: "forward"
      }
    ];
    if (CommonUtil.isMobileSize()) {
      menuActions.push({
        label: translations.SEND_TO_OWNCLOUD,
        iconName: "owncloud",
        vncIconName: "own-cloud",
        action: "ownCloud"
      });
    }
    const jid = this.fromJid || message.fromJid;
    let avatarName = this.avatarRepo.buildTargetHash(jid);
    const avatarFallback = { email: jid, fullName: this.contactRepo.getFullName(jid) };
    const avatarUrl = CommonUtil.translateHINFileURL(this.configService.avatarServiceUrl + "/" + avatarName + ".jpg?v=" + new Date().getTime());
    const document: any = {
      id: message.id,
      fileName: CommonUtil.isChannelFileURL(message.attachment.fileName) ? decodeURI(message.attachment.fileName.substr(message.attachment.fileName.lastIndexOf("/") + 1)) : message.attachment.fileName,
      fileType: this.isImage ? `image/${message.attachment.fileType}` : message.attachment.fileType,
      fullName: this.contactRepo.getFullName(jid),
      url: CommonUtil.isChannelFileURL(message.attachment.url) ? CommonUtil.addTokenToURL(CommonUtil.getAttachmentLocalAPIURL(message.attachment.url)) : message.attachment.url,
      thumbnail_url: CommonUtil.isChannelFileURL(message.attachment.url) ? CommonUtil.addTokenToURL(CommonUtil.getAttachmentLocalAPIURL(message.attachment.url)) : message.attachment.url.replace("share.php", "preview.php"),
      avatarUrl,
      avatarFallback,
      timestamp: message.timestamp
    };
    if (this.isSupportedVideo) {
      if (environment.isCordova && screen.orientation.unlock && !CommonUtil.isOnIpad() && !CommonUtil.isOnIOS()) {
        screen.orientation.unlock();
      }
      document.fileType = `video/${message.attachment.fileType}`;
    }
    let allMessages = [];
    let fileExtension = (document.fileName.split(".").pop() || "").toLowerCase();

    if ((fileExtension !== "pdf") && this.isAppOnline) {
      this.convRepo.getSelectedConversationMessagesWithContent().pipe(take(1))
        .subscribe(messages => {
          messages = messages.map(msg => {
            if (!msg.attachment && msg.body
              && (((msg.body.startsWith("http://") || msg.body.startsWith("https://")) && CommonUtil.isKnownAttachmentMediaType(msg.body))
                || CommonUtil.isChannelFileURL(msg.body))) {
              const splitPath = msg.body.split("/").filter((val) => { return val.length > 0; });
              try {
                if (splitPath.length > 2) {
                  const fileName = msg.body.split("/").pop().split("?")[0] || "";
                  const extension = fileName.toLowerCase().split(".").pop();
                  msg.attachment = {
                    url: msg.body && CommonUtil.isChannelFileURL(msg.body) ? CommonUtil.addTokenToURL(CommonUtil.getAttachmentLocalAPIURL(msg.body.substr(msg.body.indexOf(":") + 2, msg.body.length))).replace("api/api/", "api/") : msg.body,
                    fileName: fileName,
                    fileSize: 0,
                    fileType: extension
                  };
                }
              } catch (error) {
                // eslint-disable-next-line no-console
                console.warn("[previewDocument]", msg, error);
              }
            }
            if (!msg.attachment && document.id === msg.id) {
              try {
                const fileName = document.url.split("/").pop().split("?")[0];
                msg.attachment = { ...document, fileName };
              } catch (error) {
                // eslint-disable-next-line no-console
                console.warn("[previewDocument]", msg, error);
              }
            }
            return msg;
          });
          allMessages = this.mapDocuments(messages.filter(m => !m.isDeleted && m.attachment && m.attachment.fileType
            && (CommonUtil.isImage(m.attachment.fileType) || CommonUtil.isSupportedVideo(m.attachment.fileType) || CommonUtil.isAudio(m.attachment.fileType)))
            .map(m => this.mapMessageFromStore(m)));
        });
    }
    let isCommonMediaFile = fileExtension === "pdf"
      || CommonUtil.isImage(fileExtension)
      || CommonUtil.isAudio(fileExtension)
      || CommonUtil.isSupportedVideo(fileExtension);

    if (isCommonMediaFile) {
      const style: any = {
        width: "100vw",
        maxWidth: "100vw",
        height: "100vh"
      };

      let previewDialogFiles = [];
      if (fileExtension !== "pdf") {
        this.isImage = CommonUtil.isImage(message.attachment.fileType);
        this.isSupportedVideo = CommonUtil.isSupportedVideo(message.attachment.fileType);
        previewDialogFiles = this.isImage || this.isSupportedVideo || this.isAudio ? allMessages.map(v => this.mapPreviewItem(v)) : [];
        if (!previewDialogFiles.length && message.attachment) {
          const filetype = CommonUtil.isImage(fileExtension) ? "image" :
            CommonUtil.isAudio(fileExtension) ? "audio" :
              CommonUtil.isSupportedVideo(fileExtension) ? "video" : "octet-stream";
          message.attachment = { ...message.attachment, type: filetype };
          previewDialogFiles = [this.mapPreviewItem(message.attachment)];
        }
      }
      if (fileExtension === "pdf") this.headerActions = this.headerActions.filter(v => v.action !== "view");
      // if (CommonUtil.isOnMobileDevice()) {
      //   previewDialogFiles = [];
      //   let copyPreviewFiles = [];
      //   if (fileExtension !== "pdf") {
      //     this.isSupportedVideo = CommonUtil.isSupportedVideo(message.attachment.fileType);
      //     copyPreviewFiles = this.isSupportedVideo || this.isAudio ? allMessages.map(v => this.mapPreviewItem(v)) : [];
      //     if (!copyPreviewFiles.length && message.attachment) {
      //       const filetype = CommonUtil.isAudio(fileExtension) ? "audio" :
      //         CommonUtil.isSupportedVideo(fileExtension) ? "video" : "octet-stream";
      //       message.attachment = { ...message.attachment, type: filetype };
      //       copyPreviewFiles = [this.mapPreviewItem(message.attachment)];
      //     }
      //     copyPreviewFiles.map(prv => {
      //       if (CommonUtil.isAudio(prv.fileType.split("/")[1]) || CommonUtil.isSupportedVideo(prv.fileType.split("/")[1])) {
      //         previewDialogFiles.push(prv);
      //       }
      //     });
      //   }
      // }

      if (document.url.startsWith("blob:")) {
        this.databaseService.fetchPendingAttachmentById(message.id).subscribe(attMessage => {
          document.url = attMessage.b64attachment;
          document.thumbnail_url = attMessage.b64attachment;
          message.attachment.url = attMessage.b64attachment;
          previewDialogFiles = [this.mapPreviewItem(message.attachment)];
          setTimeout(() => {
            this.previewDialog = this.dialog.open(DocumentPreviewComponent, Object.assign({
              backdropClass: "vnc-dialog-backdrop",
              panelClass: "vnc-preview-dialog-panel",
              disableClose: true,
              hasBackdrop: false,
              data: {
                document: document,
                selectedId: document.id,
                showOwnCloudIcon: this.enabledOwnCloud,
                showOwnCloudText: translations.SEND_TO_OWNCLOUD,
                viewerMode: this._getViewerMode(mediaType),
                forMobile: CommonUtil.isMobileSize(),
                forMobileDevice: CommonUtil.isOnAndroid(),
                files: fileExtension === "pdf" ? [] : previewDialogFiles.reverse(),
                menuActions: menuActions,
                contextMenuActions: contextMenuActions,
                incomingActionEvent: this.outgoingActionEvent,
                headerActions: this.headerActions,
                triggerEvent: triggerEvent,
                triggerGallary: this.conversationRepo.triggerGallary,
                imageZoomSlider: true,
                escapeFromParent: true,
                menuText: this.getTranslationForSearch("MORE_TEXT"),
                closeText: this.getTranslationForSearch("CLOSE"),
              },
              autoFocus: true
            }, style));
            setTimeout(() => {
              this.changeDetectorRef.markForCheck();
            }, 200);
          }, 200);
        });

      } else {
        // if (this.isImage) {
        //     previewDialogFiles = [this.mapPreviewItem(message.attachment)];
        // }
        this.previewDialog = this.dialog.open(DocumentPreviewComponent, Object.assign({
          backdropClass: "vnc-dialog-backdrop",
          panelClass: "vnc-preview-dialog-panel",
          disableClose: true,
          hasBackdrop: false,
          data: {
            document: document,
            selectedId: document.id,
            showOwnCloudIcon: this.enabledOwnCloud,
            showOwnCloudText: translations.SEND_TO_OWNCLOUD,
            viewerMode: this._getViewerMode(mediaType),
            forMobile: CommonUtil.isMobileSize(),
            forMobileDevice: CommonUtil.isOnAndroid(),
            files: fileExtension === "pdf" ? [] : previewDialogFiles.reverse(),
            menuActions: menuActions,
            contextMenuActions: contextMenuActions,
            incomingActionEvent: this.outgoingActionEvent,
            headerActions: this.headerActions,
            triggerEvent: triggerEvent,
            triggerGallary: this.conversationRepo.triggerGallary,
            imageZoomSlider: true,
            escapeFromParent: true,
             menuText: this.getTranslationForSearch("MORE_TEXT"),
            closeText: this.getTranslationForSearch("CLOSE")
          },
          autoFocus: true
        }, style));
      }
    }
    else if (this.configService.get("onlyOfficeApiUrl")?.length) {
      const onlyOfficeUrl = this.configService.get("onlyOfficeApiUrl");
     this.translate.get("FILE_PREVIEW_NOT_AVAILABLE").pipe(take(1)).subscribe(text => {
      this.previewNotAvailableText = text;
      });
      this.headerActions = this.headerActions.filter(v => v.action !== "view");
      const style: any = {
        maxHeight: "100vh",
        maxWidth: "100vw",
        height: "100vh",
        width: "100vw",
      };
      const docFileExt = document.fileName.slice(document.fileName.lastIndexOf(".") + 1, document.fileName.length);
      if (!!docFileExt && (document.fileType !== docFileExt)) {
        document.fileType = docFileExt;
      }
      this.logger.info("[FilePreviewDialogComponent]", document, allMessages);
      this.previewDialog = this.dialog.open(DocumentPreviewComponent, Object.assign({
        panelClass: "vnc-preview-dialog-panel",
        data: {
          document: document,
          selectedId: document.id,
          forMobile: CommonUtil.isMobileSize(),
          previewNotAvailable: this.previewNotAvailableText,
          apiURL: onlyOfficeUrl,
          menuActions: menuActions,
          contextMenuActions: contextMenuActions,
          incomingActionEvent: this.outgoingActionEvent,
          headerActions: this.headerActions,
          forMobileDevice: CommonUtil.isOnAndroid(),
          triggerEvent: triggerEvent,
           menuText: this.getTranslationForSearch("MORE_TEXT"),
          closeText: this.getTranslationForSearch("CLOSE"),
        },
        autoFocus: true,
      }, style));
    }
    else {
      this.toastService.show("UNSUPPORTED_FILE");
    }

    if (!this.previewDialog) return;
    this.previewDialog.afterClosed().pipe(take(1)).subscribe(() => {
      this.conversationRepo.turnNativeElectronContextMenuOn();
      this.previewClosed$.next(false);
      this.previewClosed$.complete();
      if (environment.isCordova && screen.orientation.lock && !CommonUtil.isOnIpad() && !CommonUtil.isOnIOS()) {
        screen.orientation.lock("portrait");
      }
    });

  }

  private showSharingSpinner() {
    this.store.dispatch(new SetMediaSharingStatus(true));
  }

  private hideSharingSpinner() {
    this.store.dispatch(new SetMediaSharingStatus(false));
  }

  sharePhoto(imageUrl: string): void {
    this.logger.info("[sharePhoto]", imageUrl);

    this.showSharingSpinner();

    this.sharingService.share(imageUrl).subscribe({
      next: (res) => {
        this.logger.info("[sharePhoto] - share done", res);
        this.hideSharingSpinner();
      },
      error: () => {
        this.logger.error("[sharePhoto] - share failed");
        let transKey = "FILE_SENDING_FAILED";
        if (environment.theme === "hin") {
          transKey = "FILE_SENDING_FAILED_HIN";
        }
        this.toastService.showSnackbar(transKey, 2000);
        this.hideSharingSpinner();
      }, complete: () => {
        this.hideSharingSpinner();
      }
    });
  }

  handleTriggerEvent(event: any) {
    this.logger.info("[triggerEvent]", event);
    switch (event?.action) {
      case "email":
        this.emailTo(event.document);
        break;
      case "addToFavorites":
        if (!this.message.isStarred) {
          this.starMessage();
          this.notificationService.openSnackBarWithTranslation("ADDED_TO_FAVORITES");
        } else {
          this.unstarMessage();
          this.notificationService.openSnackBarWithTranslation("REMOVED_FROM_FAVORITES");
        }
        break;
      case "forward":
        this.forwardMessage(event.document.id);
        break;
      case "download":
        this.downloadMessage(event.document.url, event?.document);
        break;
      case "copy":
        const fileType = event?.document.type;
        if (fileType && fileType.startsWith("image")) {
          this.outgoingActionEvent.next("copy-image");
          this.notificationService.openSnackBarWithTranslation("IMAGE_COPY_SUCCESS");
        } else {
          CommonUtil.copyToClipboard([event.document.url]);
          this.notificationService.openSnackBarWithTranslation("IMAGE_COPY_SUCCESS");
        }
        break;
      case "ownCloud":
        this.saveToOwncloud(event.document);
        break;
      case "share":
          if (CommonUtil.isOnNativeMobileDevice()) {
            this.sharePhoto(event.document.url);
          } else {
            this.underDevelopment();
          }
          break;
      case "doubletap":
        if (event.document.type === "image") {
          this.conversationRepo.triggerGallary.next("zoom");
        }
        break;
      case "view":
        this.isGridMode();
        break;
      case "indexChange":
          break;
      case "copy-image":
        this.outgoingActionEvent.next("copy-image");
        break;
      case "image-copy-success-keyboard-shortcut":
      case "image-copy-success-trigger-event":
        this.notificationService.openSnackBarWithTranslation("IMAGE_COPY_SUCCESS");
        break;
      case "image-copy-error":
        this.notificationService.openSnackBarWithTranslation("UNABLE_TO_COPY_FILE");
        break;
      case "gotoSource":
        this.goToSource(event?.document);
        break;
      case "escape":
        this.closePreviewDialog();
        break;
      case "mobile-zoom-image":
        if (environment.isCordova && screen.orientation.unlock) {
           screen.orientation.unlock();
        }

         this.conversationRepo.fullviewImageMobile = this.dialog.open(FullViewImageComponent, Object.assign({
            data: {imageURL:event.document.url},
            backdropClass: "vnctalk-form-backdrop",
             panelClass: ["vnctalk-form-panel", "full-view-image-panel"],
            disableClose: true,
            autoFocus: true
          }));

        break;
      default:
        this.underDevelopment();
        break;
    }
  }
  resizeTimeout: any;
  @HostListener("window:resize" , ["$event"])
  onResize(event: Event) {
    if (this.viewGridMode) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.setGalleryView();
      }, 300);
    }
    this.shouldApplyClassOnResize();
  }

  isGridMode() {
    const galeryBigImage = document.querySelector(".g-box");
    const gallerySliderContainer = document.querySelector(".preview-body");
    const zoomControl = document.querySelector(".zoom-control-wrapper");

    this.viewGridMode = !this.viewGridMode;
    if (this.viewGridMode) {
      if (gallerySliderContainer) gallerySliderContainer?.classList.add("grid-mode-container");
      if (galeryBigImage) galeryBigImage?.classList.add("grid-big-image");
      if (zoomControl) zoomControl?.classList?.add("hide-zoom-control");
      this.headerActions[5].label = this.translations.GALLERY_VIEW;
      this.headerActions[5].vncIconName = "grid-mobile-stripe-new";
      this.setGalleryThumbIndex();
      this.setGalleryView();
    } else {
      if (gallerySliderContainer) gallerySliderContainer?.classList?.remove("grid-mode-container");
      if (galeryBigImage) galeryBigImage?.classList?.remove("grid-big-image");
      if (zoomControl) zoomControl?.classList?.remove("hide-zoom-control");
      this.headerActions[5].label = this.translations?.GRID_VIEW;
      this.headerActions[5].vncIconName = "view-tile-grid";
      this.setGridView();
    }
  }

  setGalleryThumbIndex() {
    const galleryThumbEle = document.getElementsByTagName("gallery-thumb");
    if (galleryThumbEle.length > 0) {
      const galleryThumbEArray = Array.from(galleryThumbEle);
      if (galleryThumbEArray.length > 0) {
        galleryThumbEArray.forEach((item ,index)=> {
          item.id = `thumb-${index}`;
        });
      };
    };
  }

  setGridView() {
    const galleryThumbList = [];
    const gSlider = document.getElementsByClassName("g-slider")[0];
    const colList = gSlider.querySelectorAll(".grid-column");

    if (colList.length > 0) {
      colList.forEach((element,index) => {
        const col = gSlider.querySelector(`.grid-column-${index + 1}`);
        if (col && col?.children?.length > 0) {
          Array.from(col?.children).forEach((element) => {
            element["style"].height = "auto";
            const imageDiv = element.getElementsByClassName("g-image-item")[0];
            if (imageDiv) imageDiv["style"].backgroundPosition = "center";
            galleryThumbList.push(element);
          });
        };
        gSlider.removeChild(col);
      });
    };

    galleryThumbList.sort((a, b) => {
      const eleA = parseInt(a.id.split("-")[1]);
      const eleB = parseInt(b.id.split("-")[1]);
      return eleA - eleB;
    });

    galleryThumbList.forEach(element => {
      gSlider.appendChild(element);
    });
  }

  setGalleryView() {
    const galleryThumbEle = document.getElementsByTagName("gallery-thumb");
    const galleryThumbEArray = Array.from(galleryThumbEle);
    const galleryList = [];
    const {columnwidth, numberOfCols} = this.createColumn();
    if (galleryThumbEArray.length > 0) {
      galleryThumbEArray.forEach((element, index) => {
        const imgItemElement = galleryThumbEArray[index].getElementsByClassName("g-image-item")[0];
        const imgErrorElement = galleryThumbEArray[index].getElementsByClassName("g-image-error-message")[0];
        if (imgErrorElement instanceof HTMLElement) {
          const galleryThumb = document.getElementsByTagName("gallery-thumb");
          if (galleryThumb && galleryThumb[index] && galleryThumb[index]["style"]) {
            const aspectRatio = columnwidth / 300;
            const height = columnwidth / aspectRatio;
            galleryThumb[index]["style"].height = height + "px" ;
            galleryList.push(galleryThumb[index]);
            if ((index === galleryThumbEArray.length - 1)) {
              this.insertImageToGallery(galleryList,numberOfCols);
            }
          }
        }
        if (imgItemElement instanceof HTMLElement) {
          const url = imgItemElement.outerHTML;
          if (url) {
            const regex = /url\(&quot;([^&]+)&quot;\);/;
            const match = regex.exec(url);
            if (match) {
              const urlValue = match[1];
              const image = new Image();
              image.src = urlValue;
              image.onload = () => {
                const GSlider = document.getElementsByClassName("g-slider")[0];
                const colWidth = GSlider.querySelector(".grid-column-1").clientWidth;
                const galleryThumb = document.getElementsByTagName("gallery-thumb");
                const imageDiv = galleryThumb[index].getElementsByClassName("g-image-item")[0];
                if (galleryThumb && galleryThumb[index] && galleryThumb[index]["style"]) {
                  const aspectRatio = image.width / image.height;
                  const height = colWidth / aspectRatio;
                  if (height < 560) {
                    if (height < (colWidth / 3)) {
                      galleryThumb[index]["style"].height = (colWidth / 3) + "px" ;
                      imageDiv["style"].backgroundPosition = "left";
                    }
                    else galleryThumb[index]["style"].height = height + "px" ;
                  }
                  else {
                    galleryThumb[index]["style"].height = "560px";
                    imageDiv["style"].backgroundPosition = "top";
                  }
                  galleryList.push(galleryThumb[index]);
                  if ((index === galleryThumbEArray.length - 1)) {
                    this.insertImageToGallery(galleryList,numberOfCols);
                  }
                }
              };
            }
          }
        }
      });
    }
  }

  createColumn() {
    let columnwidth;
    const gSlider = document.getElementsByClassName("g-slider")[0];
    let numberOfCols = 8;
    if (gSlider) {
      const screenWidth = window.innerWidth;
      if (screenWidth < 960) numberOfCols = 3;
      else if (screenWidth > 960 && screenWidth < 1639) numberOfCols = 4;
      else if (screenWidth > 1640 && screenWidth < 1921) numberOfCols = 5;
      else numberOfCols = 6;
    }
    gSlider["style"].gridTemplateColumns = `repeat(${numberOfCols}, minmax(0, 1fr)`;
    if (numberOfCols === 6 ) gSlider["style"].padding = "0 100px";
    else gSlider["style"].padding = "0";
    for (let i = 1; i <= numberOfCols; i++) {
      const columnClass = `grid-column-${i}`;
      if (!gSlider.querySelector(`.${columnClass}`)) {
          const column = document.createElement("div");
          column.classList.add("grid-column");
          column.classList.add(columnClass);
          gSlider.appendChild(column);
          if (i === 1) columnwidth = column.clientWidth;
      }
      else columnwidth = gSlider.querySelector(`.${columnClass}`)?.clientWidth;
    }
    return {columnwidth, numberOfCols};
  }

  insertImageToGallery(galleryList,numberOfCols) {
      galleryList.sort((a, b) => {
        const indexA = parseInt(a.id.split("-")[1]);
        const indexB = parseInt(b.id.split("-")[1]);
        return indexA - indexB;
    });
    const gSlider = document.getElementsByClassName("g-slider")[0];
    const columnsArr = gSlider.querySelectorAll(".grid-column");
    if (columnsArr?.length > 0) {
      columnsArr.forEach(ele => {
        while (ele?.firstChild) {
          ele.removeChild(ele.firstChild);
        };
      });
    };
    galleryList.forEach((element) => {
      const indexToRemove =  Array.from(gSlider.children).findIndex(i => i["__ngContext__"] === element["__ngContext__"]);
      if (indexToRemove !== -1) gSlider.removeChild(element);
      const colList = gSlider.querySelectorAll(".grid-column");
      if (colList.length > 0) {
        let colHieght = Array.from(colList).map((e) => {
          const i = parseInt(e.className.split("-").pop());
          if (i <= numberOfCols) {
            return {
              height: e.clientHeight,
              colEle: e
            };
          }
          else {
            return null;
          }
        }).filter(Boolean);
        if (colHieght.length > 0) {
          colHieght.sort((a, b) => a.height - b.height);
          colHieght[0].colEle.appendChild(element);
        };
      };
    });
  }

  private mapDocuments(docs) {
    this.logger.info("[mapDocuments]", docs);
    return docs.filter(v => !!v).map(doc => {
        const body = doc.body ? doc.body.split("\n")[0] : "";
        let fileName = body.slice(body.lastIndexOf("/") + 1, body.length);
        let fileSize = "";
        let fileSizeNumber = "";
        let fileType = fileName ? fileName.slice(fileName.lastIndexOf(".") + 1, fileName.length) : "";
        let iconName = "mdi-file-document-box";
        let timestamp = doc.timestamp;
        let type = "document";
        let url = body;
        if (doc.x_attachment) {
            const attachment = JSON.parse(doc.x_attachment);
            if (attachment.fileName) {
                fileName = attachment.fileName;
            }

            if (attachment.url) {
                url = attachment.url;
            }
            if (attachment.fileSize) {
                try {
                    fileSize = filesize(attachment.fileSize, {locale: this.lang});
                    fileSizeNumber = attachment.fileSize;
                } catch (ex) {

                }
            }
            if (attachment.fileType) {
                fileType = attachment.fileType;
            }
        } else if (doc.attachment) {
            fileName = doc.attachment.fileName;
            url = doc.attachment.url;
            if (doc.attachment.fileSize) {
                fileSizeNumber = doc.attachment.fileSize;
                try {
                    fileSize = filesize(doc.attachments.fileSize, {locale: this.lang});
                } catch (ex) {
                  this.logger.sentryErrorLog("convert attachment file size ex", ex);
                }
            }
            fileType = fileName.slice(fileName.lastIndexOf(".") + 1, fileName.length);
        }
        if (!fileType) {
            fileType = "";
        }
        if (!fileName) {
            fileName = "";
        }
        if (CommonUtil.isAudio(fileType)) {
            iconName = "mdi-file-music";
            type = "audio";
        } else if (CommonUtil.isVideo(fileType)) {
            iconName = "mdi-video";
            type = "video";
        } else if (CommonUtil.isImage(fileType)) {
            iconName = "mdi-image";
            type = "image";
        } else if (fileName.toLowerCase().indexOf(".pdf") !== -1) {
            iconName = "mdi-file-pdf";
        } else if (fileName.toLowerCase().indexOf(".ppt") !== -1 || fileName.toLowerCase().indexOf(".pptx") !== -1) {
            iconName = "mdi-file-pdf-box";
        } else if (fileName.toLowerCase().indexOf(".doc") !== -1 || fileName.toLowerCase().indexOf(".docx") !== -1) {
            iconName = "mdi-file-word-box";
        } else if (fileName.toLowerCase().indexOf(".xls") !== -1 || fileName.toLowerCase().indexOf(".xlsx") !== -1) {
            iconName = "mdi-file-excel-box";
        }
        return {
            id: doc.id,
            doc: doc,
            type: type,
            url: url,
            thumbnail: url.replace("share.php", "preview.php"),
            fileName: fileName,
            fullName: this.contactRepo.getFullName(doc.from),
            jid: doc.from,
            fileSize: fileSize,
            fileSizeNumber: fileSizeNumber,
            timestamp: timestamp,
            fileType: fileType,
            iconName: iconName
        };
    });
  }

  private mapPreviewItem(data) {
    // let avatarName = this.avatarRepo.buildTargetHash(data.jid);
    this.logger.info("mapPreviewItem: ", data);
    const avatarFallback = {email: data.jid, fullName: this.contactRepo.getFullName(data.jid)};
    const avatarUrl = this.avatarRepo.getAvatarUrlWithCache(data.jid);
    const document: any = {
    fileName: data.fileName,
    type: data.type,
    fileType: data.type + "/" + (data.fileType === "mov" ? "mp4" : data.fileType),
    fullName: this.contactRepo.getFullName(data.jid),
    url: data.url,
    thumbnail_url: data.url ? data.url.replace("share.php", "preview.php") : "",
    avatarUrl,
    avatarFallback,
    id: data.id,
    timestamp: data.timestamp};
    if (data?.fileSizeNumber) document["fileSizeNumber"] = data.fileSizeNumber;
    return document;
  }

  private mapMessageFromStore(m) {
    const sm: any = {
        id: m.id,
        from: m.fromJid,
        to: m.to.bare,
        body: m.body,
        timestamp: m.timestamp,
        x_attachment: JSON.stringify(m.attachment),
        x_origMessage: JSON.stringify(m.originalMessage),
        x_forwardMessage: JSON.stringify(m.forwardMessage)
    };
    return sm;
}

  processImageVideoAudioAttachment() {
    if (!this.message || !this.message?.attachment || !this.message?.attachment?.url) {
      return;
    }
    this.isAudio = CommonUtil.isAudio(this.message.attachment.fileType);
    this.isImage = CommonUtil.isImage(this.message.attachment.fileType);
    this.isDocument = CommonUtil.isDocument(this.message.attachment.fileType);
    this.isSupportedVideo = CommonUtil.isSupportedVideo(this.message.attachment.fileType);
    this.isVCFfile = CommonUtil.isVCFfile(this.message.attachment.fileType);

    // URL sanitazing
    let translatedUrl = CommonUtil.translateHINFileURL(this.message.attachment.url);
    this.message.attachment.url = translatedUrl;
    this.translatedUrl = translatedUrl;

    const isLimitSizeImage = (this.isImage || this.isSupportedVideo) && +this.message.attachment.fileSize > (1000 * 1024 * 5);

    const url = this.message.attachment.url || "";
    this.attachmentUrl = this.trustURL(`${url}`);
    if (!CommonUtil.isOnNativeMobileDevice() && CommonUtil.isVideo(this.message.attachment.fileType) && url.indexOf("blob:") === -1) {
      this.attachmentUrl = this.trustURL(`${url}?d=true`);
    }

    // Image
    if (this.isImage) {
      if (this.message.attachment.url.includes("/share.php/") && url.toLowerCase().lastIndexOf(".png") === -1 && url.lastIndexOf(".svg") === -1 && url.toLowerCase().lastIndexOf(".gif") === -1 ) {
        this.imagePreviewUrl = this.trustURL(this.messageService.getAttachmentPreviewUrl(this.message.attachment.url));
      } else if (this.message.attachment.fileType === MessageComponent.SVG_TYPE || this.message.attachment.fileType === MessageComponent.GIF_TYPE) {
        this.imagePreviewUrl = this.attachmentUrl;
      }
      // form localUrl for mobile
      if (CommonUtil.isOnNativeMobileDevice() && !this.isUrlLocalBlob(this.message.attachment.url) && !this.isLoading) {
        const imagePreviewUrl = this.messageService.getAttachmentPreviewUrl(this.message.attachment.url);
        const attachmentUrl = isLimitSizeImage
            ? (imagePreviewUrl ? imagePreviewUrl : this.attachmentUrl)
            : this.attachmentUrl;
        this.formLocalUrl(attachmentUrl);
      }
    }

    // Video
    if (this.isSupportedVideo && this.message.attachment.url.includes("/share.php/")) {
      const tempUrl = (this.message.attachment.url.indexOf("?") > -1) ? this.message.attachment.url.split("?")[0] : this.message.attachment.url;
      this.videoPreviewUrl = this.messageService.getAttachmentPreviewUrl(tempUrl);
    }

    // Audio
    if (this.isAudio && CommonUtil.isOnNativeMobileDevice() && !this.isUrlLocalBlob(this.message.attachment.url) && !this.isLoading) {
      this.formLocalUrl(url);
    }
    if (this.hideInsecureContent && this.message.attachment.url && this.isInsecureUrl(this.message.attachment.url)) {
      this.isInsecureContent = true;
    } else {
      this.isInsecureContent = false;
    }
    // this.logger.info("[MessageComponent][processImageVideoAudioAttachment]", this.message.id, this.message.attachment, this.isInsecureContent, this.hideInsecureContent);
  }

  isInsecureUrl(url) {
    return !url.startsWith("https") && !(url.startsWith("file://") || url.startsWith("blob:"));
  }

  private onImagePreviewLoaded() {
    const imgEl = <HTMLElement>document.querySelector(`.image-attachment-preview-${this.message.id}`);
    if (!imgEl) {
      // eslint-disable-next-line no-console
      console.warn("[MessageComponent][onImagePreviewLoaded]", `.image-attachment-preview-${this.message.id}`, "not found", this.message);
      return;
    }

    const imgHeight = imgEl.clientHeight;
    const imgWidth = imgEl.clientWidth;

    // Apply styles based on the image's aspect ratio
    if (imgWidth > imgHeight) {
      imgEl.style.height = imgHeight + "px";

      const imgPreviewEl = <HTMLElement>document.querySelector(`.image-wrapper-${this.message.id}`);
      if (imgPreviewEl) {
        imgPreviewEl.style.height = imgHeight + "px";
      }
    } else {
      this.logger.info("[MessageComponent][onImagePreviewLoaded]: portrait", imgHeight, imgWidth, this.message.id);
    }
    if (imgWidth < 320) {
      const container = imgEl.closest(".image-container") as HTMLElement;
      if (container) {
        const backgroundOverlay = container.querySelector(".background-overlay") as HTMLElement;
        if (backgroundOverlay) {
          backgroundOverlay.style.opacity = "0.3";
          backgroundOverlay.style.filter = "blur(32px)";
        }
      }
    }
  }


  private onVideoPreviewLoaded() {
    const videoPreviewEl = (<HTMLElement>document.querySelector(`.video-attachment-preview-${this.message.id}`));
    if (!videoPreviewEl) {
      // eslint-disable-next-line no-console
      console.warn("[MessageComponent][onVideoPreviewLoaded]", `.video-attachment-preview-${this.message.id}`, "not found", this.message);
      return;
    }

    const videoHeight = 84;
    const videoWidth = videoPreviewEl.clientWidth;

    if (videoWidth > videoHeight && videoHeight < ATTACHMENT_IMAGE_MAX_HEIGHT_PORTRAIT) {
      const videoEl = (<HTMLElement>document.querySelector(`.video-player-${this.message.id}`));
      this.videoHeight = 84;
      this.videoWidth = videoWidth;
      this.logger.info("[MessageComponent][onVideoPreviewLoad]: landscape", videoHeight, videoWidth, this.message.id);
    } else {
      this.videoHeight = ATTACHMENT_VIDEO_MAX_HEIGHT_PORTRAIT;
      const ratio = (videoHeight / videoWidth);
      this.videoWidth = ATTACHMENT_VIDEO_MAX_HEIGHT_PORTRAIT / ratio;
      this.logger.info("[MessageComponent][onVideoPreviewLoad]: portrait", videoHeight, videoWidth, this.message.id);
    }
  }

  private onVideoReplyPreviewLoaded(){
    const videoPreviewEl = (<HTMLElement>document.querySelector(`.video-attachment-reply-preview-${this.message.id}`));
    const videoHeight = videoPreviewEl.clientHeight;
    const videoWidth = videoPreviewEl.clientWidth;

    if (videoWidth > videoHeight && videoHeight < ATTACHMENT_IMAGE_MAX_HEIGHT_PORTRAIT) {
      const videoEl = (<HTMLElement>document.querySelector(`.video-player-reply-${this.message.id}`));
      videoEl.style.height = videoHeight + "px";
      this.videoHeight = videoHeight;
      this.videoWidth = videoWidth;
      this.logger.info("[MessageComponent][onVideoReplyPreviewLoaded]: landscape", videoHeight, videoWidth, this.message.id);
    } else {
      this.videoHeight = ATTACHMENT_VIDEO_MAX_HEIGHT_PORTRAIT;
      const ratio = (videoHeight / videoWidth);
      this.videoWidth = ATTACHMENT_VIDEO_MAX_HEIGHT_PORTRAIT / ratio;
      this.logger.info("[MessageComponent][onVideoReplyPreviewLoaded]: portrait", videoHeight, videoWidth, this.message.id);
    }
  }

  private formLocalUrl(remoteUrl: any){
    this.logger.info("[formLocalUrl]", remoteUrl);
    this.isLoading = true;
    if (CommonUtil.isOnIOS()) {
      this.databaseService
      .getAttachmentUrl(remoteUrl)
      .pipe(takeUntil(this.isAlive$))
      .subscribe(url => {
        if (!!url) {
          window.resolveLocalFileSystemURL(url, (fileEntry: any) => {
               fileEntry.file((file) => {
                 let reader = new FileReader();
                 let self = this;
                 reader.onloadend = function () {
                   self.localUrl = this.result;
                   // this.logger.info("[MessageComponent] localURl IOS", self.localUrl);
                   self.isLoading = false;
                   self.changeDetectorRef.markForCheck();
                 };
                 reader.readAsDataURL(file);
               }, (e) => {
                 this.logger.error(e);
                 this.isLoading = false;
                  this.localUrl = remoteUrl;
                  this.changeDetectorRef.markForCheck();
               });
             }, (e) => {
            this.logger.error(e);
            this.isLoading = false;
            this.localUrl = remoteUrl;
            this.changeDetectorRef.markForCheck();
           });
        } else {
          remoteUrl = CommonUtil.translateHINFileURL(remoteUrl);
          this.isLoading = false;
          this.localUrl = remoteUrl;
          this.changeDetectorRef.markForCheck();
        }
      });
    } else {
      remoteUrl = CommonUtil.translateHINFileURL(remoteUrl);
      this.isLoading = false;
      this.localUrl = remoteUrl;
      this.changeDetectorRef.markForCheck();
    }
  }

  async openVideoPlayer() {
      let dialogStyles = {
          "width": "auto",
          "height": (window.innerHeight - 50) + "px",
          "maxHeight": (window.innerHeight - 50) + "px",
          "maxWidth": window.innerWidth + "px",
          "visibility": "visible"
      };
      if (CommonUtil.isOnMobileDevice()) {
        dialogStyles = {
          "width": "100vw",
          "height": "100vh",
          "maxHeight": "100vh",
          "maxWidth": "100vw",
          "visibility": "visible"
      };
      }
      const { MediaPreviewDialogComponent } = await import(
        "../dialogs/media-preview/media-preview.component");
      this.dialog.open(MediaPreviewDialogComponent, Object.assign({
        data: {message: {...this.message, isOriginalMessage: true}, mediaType: "videos"},
        backdropClass: "vnctalk-form-backdrop",
        panelClass: "vnctalk-form-panel",
        disableClose: true,
        autoFocus: true
      }, dialogStyles));

  }

  playVideo(): void {
    this.logger.info("[MessageComponent][playVideo]", this.message.checkedVideo, this.attachmentUrl);

    this.displayPlayer = true;

    if (this.message.checkedVideo) {
      this.showPlayer = "block";
      this.isVideoLoading = false;
      this.changeDetectorRef.markForCheck();

    } else if (this.message.attachment && this.message.attachment.fileType && !this.isUrlLocalBlob(this.message.attachment.url)) {
      if (this.attachmentUrl) {
        let totalTried = 0;
        this.isVideoLoading = true;

        if (this.checkPlayable) {
          clearInterval(this.checkPlayable);
        }
        this.checkPlayable = setInterval(() => {
          this.logger.info("[MessageComponent][playVideo]", this.videoPlayer, this.localUrl, this.videoPlayer ? this.videoPlayer.nativeElement.readyState : null);

          if (this.videoPlayer) {
            if (this.videoPlayer.nativeElement.readyState > 2 || (CommonUtil.isOnIOS() && this.videoPlayer.nativeElement.readyState === 1)) {
              this.showPlayer = "block";
              this.isVideoLoading = false;
              this.message.checkedVideo = true;
              this.store.dispatch(new MessageUpdateAction({id: this.message.id, changes: {checkedVideo: true}}));
              this.changeDetectorRef.markForCheck();
              clearInterval(this.checkPlayable);
            }
          }
          totalTried++;
          if (totalTried === TOTAL_VIDEO_DETECT_TIME) {
            this.errorMessage = "UNSUPPORTED_FORMAT";
            this.isVideoLoading = false;
            this.changeDetectorRef.markForCheck();
            clearInterval(this.checkPlayable);
          }
        }, 3000);
      }
    }
  }

  private processMessageUrl(): void {
    // it's already an attachment
    const isAttachment = this.message && this.message.attachment && this.message.attachment.url && this.message.attachment.fileType;
    if (isAttachment || !this.message) {
      return;
    }

    const body = this.message.originalMessage ? this.message.originalMessage.replyMessage : this.message.body;
    const [fullUrl, urlsCount] = MessageUtil.extractUrlFromMessageBodyAndCountUrls(body, this.configService.get("jitsiURL"));
    this.fullUrl = fullUrl;
    this.urlsCount = urlsCount;

    // not an url preview
    if (!fullUrl) {
      return;
    }
    // this.logger.info("processMessageUrl", fullUrl, this.messageUrlPreviewData, this.message.urlPreviewData);
    // it's already url preview
    if (this.messageUrlPreviewData || this.message.urlPreviewData) {
      if (!this.messageUrlPreviewData && this.message.urlPreviewData) {
        this.messageUrlPreviewData = this.message.urlPreviewData;
      }

      this.isUrlPreview = true;

      // hide insecure content
      this.setIsInsecureContentForUrlPreview();
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.logger.info("[MessageComponent][processMessageUrl] this.messageRedminePreview", this.messageRedminePreview, this.message.redminePreview, fullUrl, fullUrl?.endsWith("/edit"));

    let shouldInvalidateUrlCache = false;

    // it's already redmine preview
    // if (this.messageRedminePreview || this.message.redminePreview) {
    //   if (this.message.redminePreview) {
    //     this.messageRedminePreview = this.message.redminePreview;
    //   }

    //   if (!this.messageRedminePreview.notAvailable && !fullUrl?.endsWith("/edit")) {
    //     const unfurledAt = this.messageRedminePreview.unfurledAt || (Date.now() - 10000000); // just set in past if not provided
    //     const unfurledMore10MinsAgo = (Date.now() - (+unfurledAt) > 600000);
    //     this.logger.info("[MessageComponent][processMessageUrl] unfurledAt", unfurledAt, Date.now(), Date.now() - (+unfurledAt), unfurledMore10MinsAgo, this.messageRedminePreview);
    //     if (unfurledAt) {
    //       if (!unfurledMore10MinsAgo) {
    //         this.isRedmineUrl = true;
    //         this.issueId = this.messageRedminePreview.id;
    //         this.dueDate = this.messageRedminePreview.due_date;

    //         return;
    //       } else {
    //         shouldInvalidateUrlCache = true;
    //       }
    //     }
    //   } else {
    //     this.isRedmineUrl = false;
    //     return;
    //   }
    // }


    // this.logger.info("[MessageComponent][extractUrlFromMessageBody]",  body, fullUrl);

    const { isImage, isAudio, isSupportedVideo, fileExtension, isRedmineUrl, isVersionUrl } = MessageUtil.getLinkType(fullUrl, urlsCount, this.configService.REDMINE_URL);
    const otherFileExt = this.getExtentionFromUrl(fullUrl);
    // if this is attachment link
    // TODO: maybe move it outside of MessageComponent and do not parse every time
    if (isImage || isAudio || isSupportedVideo || (otherFileExt && this.message.attachment)) {
      if (otherFileExt) {
        const encodedFileName = fullUrl?.split("/")[fullUrl?.split("/").length - 1];
        let filename = "";
        if (encodedFileName) {
          try {
            filename = decodeURIComponent(encodedFileName);
          } catch (error) {
            filename = encodedFileName;
          };
        } else filename = fullUrl;

        this.processImageVideoAudioAttachment();
      }
      else {
        this.isImage = isImage;
        this.isAudio = isAudio;
        this.isSupportedVideo = isSupportedVideo;
        if (!isAudio) {
          this.message.attachment = { fileType: fileExtension, url: fullUrl, fileName: fullUrl, fileSize: null };
          this.processImageVideoAudioAttachment();
        } else {
          fetch(fullUrl, { method: "head" }).then(async (res) => {
            const audioFileSize = await res.headers.get("content-length");
            this.logger.info("checkAudioFile audioFileSize: ", audioFileSize);
            if (parseInt(audioFileSize, 10) < 20000000) {
              this.message.attachment = { fileType: fileExtension, url: fullUrl, fileName: fullUrl, fileSize: null };
              this.processImageVideoAudioAttachment();
            }
          }).catch(err => {
            this.logger.info("checkAudio error trying to fetch ", fullUrl, err);
          });
        }
      }
      return;
    }

    // if a message does not contain the preview metadata, then let's get it and parse the old way

    this.isRedmineUrl = isRedmineUrl;
    this.isVersionUrl = isVersionUrl;

    let newUrl = fullUrl;
    if (this.isRedmineUrl) {
      newUrl = fullUrl.split("\n")[0];
    } else if (this.isVersionUrl) {
      newUrl = fullUrl.split("\n")[0];
    }

    // this.logger.info("[MessageComponent] unfurlURL", newUrl, this.appService.isAppOnline, MessageUtil.shouldUnfurlURL(newUrl), this.message);

    if (MessageUtil.shouldUnfurlURL(newUrl)) {
      if (!this.isRedmineUrl && !this.isVersionUrl) {
        this.isUrlPreview = true;
      }
      if (!this.isAppOnline) {
        this.previewNotAvailableOffline = true;
      } else {
        this.previewNotAvailableOffline = false;
        this.isPreviewLoading = true;
        if (this.isRedmineUrl) {
          shouldInvalidateUrlCache = true;
        }
        this.convRepo.unfurlURLForMessage(this.message.id, newUrl, shouldInvalidateUrlCache).subscribe(data => {
          const {res, messageId} = data;
          this.logger.info("[MessageComponent] unfurlURL RES", res, this.message.id, messageId);
          if (!!res && this.message.id === messageId) {
            if (res.unfurledData) {
              this.setUnfurledData(res.unfurledData);
            } else if (res.redmineData) {
              this.setRedmineData(res.redmineData, false);
            } else if (res.versionData) {
              this.setRedmineVersionData(res.versionData, false);
            }
            if (isRedmineUrl && !res.redmineData) {
              this.setRedmineData({notAvailable: true}, true);
            } else if (isVersionUrl && !res.versionData) {
              this.setRedmineVersionData({notAvailable: true}, true);
            }
          } else {
            // eslint-disable-next-line no-console
            console.warn("[MessageComponent] ignore unfurlURL response - not for this message");
          }
        }, err => {
          this.isPreviewLoading = false;
          this.logger.error("[MessageComponent] unfurlURL ERR", err);
          if (isRedmineUrl) {
            this.setRedmineData({notAvailable: true}, true);
          } else if (isVersionUrl) {
            this.setRedmineVersionData({notAvailable: true}, true);
          }
        });
      }

    }
  }

 messageData() {
  if (this.processURL$) {
    clearTimeout(this.processURL$);
  }
  this.processURL$ = setTimeout(() => {
    this.processMessageUrl();
  }, 200);
 }




  priorityClass() {
    const prioName = this.messageRedminePreview?.priority?.name || "";
    switch (prioName) {
      case "Low":
      case "Niedrig":
        return "low-priority";
      case "Normal":
        return "normal-priority";
      case "High":
      case "Hoch":
        return "high-priority";
      case "Urgent":
      case "Dringend":
        return "urgent-priority";
      case "Immediate":
      case "Sofort":
        return "immediate-priority";
      default:
        return "low-priority";
    }
  }

  private isUrlLocalBlob(url: string) {
    return url && url.indexOf("blob:") !== -1;
  }

  imgLoadOnError() {
    this.showDefaultAvatar = true;
    this.changeDetectorRef.markForCheck();
  }

  private processOriginalMessage() {
    if (!this.message.originalMessage) {
      return;
    }

    let highlightedBare = "";
    this.store.select(getActiveTab).pipe(take(1)).subscribe(tabName => {
      if (tabName === "mention" && this.userJID) {
        highlightedBare = this.userJID?.bare;
      }
    });
    this.store.select(getActiveFilter).pipe(take(1)).subscribe(tabName => {
      if (tabName === "mention" && this.userJID) {
        highlightedBare = this.userJID?.bare;
      }
    });
    let body: string;
    if (this.message.originalMessage?.body && !this.message.originalMessage?.htmlBody) {
      body = CommonUtil.unEscapeHTMLString(this.message.originalMessage.body.toString().replace(/&/g, "&amp;"));
    } else if (this.message.originalMessage?.htmlBody) {
      body = CommonUtil.unEscapeHTMLString(this.message.originalMessage.htmlBody.toString().replace(/&/g, "&amp;"));
    }
    this.logger.info("origMessageObject", this.message.originalMessage);
    let origMessageObject: OriginalMessage = {
      ...this.message.originalMessage,
      replyMessage: this.message.originalMessage?.replyMessage ? this.message.originalMessage.replyMessage.toString().replace(/&/g, "&amp;") : "",
      body: body
    };

    let { repliedMessage, originalMessageHTML, onlyText, repliedAudioUrl } = this.messageService.renderRepliedMessage(origMessageObject, this.message.references);
    if (CommonUtil.parseMentions(repliedMessage).length > 0 || CommonUtil.parseMentions(originalMessageHTML).length > 0
    || (this.message.type === "groupchat") && (repliedMessage.match(/@all\b/ig) || originalMessageHTML.match(/@all\b/ig))) {
      repliedMessage = this.contactRepo.renderMentionUsers(repliedMessage, "html", this.message.references, highlightedBare);
      originalMessageHTML = this.contactRepo.renderMentionUsers(originalMessageHTML, "html", this.message.references, highlightedBare);
    }
    let translations: any = {};
    this.translate.get(["TICKET_MENTION_RECEIVER_MESSAGE", "TICKET_MENTION_SENDER_MESSAGE", "META_TASK_MENTION_RECEIVER_MESSAGE", "META_TASK_MENTION_SENDER_MESSAGE"]).pipe(take(1)).subscribe(v => {
      translations = v;
    });
    if (!originalMessageHTML) {
      originalMessageHTML = "";
    }
    if ((repliedMessage.indexOf("<br />") > -1) || (repliedMessage.indexOf("<br>") > -1) || (repliedMessage.indexOf("<p>") > -1)) {
      repliedMessage = CommonUtil.removeLeadingTrimmedBreaks(repliedMessage);
      repliedMessage = CommonUtil.removeTrailingTrimmedBreaks(repliedMessage);
      this.logger.info("[MessageComponent][CHANGESPROCESSED] repliedMessage: |" + repliedMessage + "|", this.message.id);
    }

    this.repliedMessage = this.sanitizer.bypassSecurityTrustHtml(repliedMessage);
    this.repliedAudioUrl = repliedAudioUrl;
    this.onlyText = onlyText;
    try {
      if (CommonUtil.parseRedmineMentions(TICKET_MENTION, originalMessageHTML).length > 0) {
        const textToReplace = CommonUtil.parseRedmineMentions(TICKET_MENTION, originalMessageHTML)[0];
        let userjid = textToReplace.split("#")[1];
        let mentionText = translations.TICKET_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.contactRepo.getFullName(this.message.originalMessage.from));
        if (this.userJID?.bare !== userjid) {
          mentionText = translations.TICKET_MENTION_SENDER_MESSAGE.replace("{{ receiver_name }}", this.contactRepo.getFullName(userjid));
        }
        originalMessageHTML = originalMessageHTML.replace(textToReplace, mentionText);
        this.isMentionedMessage = true;
      } else if (CommonUtil.parseRedmineMentions(META_TASK_MENTION, originalMessageHTML).length > 0) {
        const textToReplace = CommonUtil.parseRedmineMentions(META_TASK_MENTION, originalMessageHTML)[0] || "";
        let userjid = textToReplace.split("#")[1];
        let mentionText = translations.META_TASK_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.contactRepo.getFullName(this.message.originalMessage.from));
        if (this.userJID?.bare !== userjid) {
          mentionText = translations.META_TASK_MENTION_SENDER_MESSAGE.replace("{{ receiver_name }}", this.contactRepo.getFullName(userjid));
        }
        originalMessageHTML = originalMessageHTML.replace(textToReplace, mentionText);
        this.isMentionedMessage = true;

      }
    } catch (error) {

    }
    if ((originalMessageHTML.indexOf("<br />") > -1) || (originalMessageHTML.indexOf("<br>") > -1) || (originalMessageHTML.indexOf("<p>") > -1)) {
      if (originalMessageHTML.indexOf("------------- ") > -1) {
        const origParts = originalMessageHTML.split("------------- ");
        let newHeaders = [];
        let newParts = [];
        origParts.forEach(opart => {
          opart = CommonUtil.removeLeadingTrimmedBreaks(opart);
          opart = CommonUtil.removeTrailingTrimmedBreaks(opart);
          newHeaders.push("<blockquote> ");
          newParts.push("<p> " + opart + " </p> </blockquote>");
        });
        originalMessageHTML = newHeaders.join(" ") + newParts.join(" ");
        // originalMessageHTML = newParts.join("<br>------------- <br>");
      } else {
        originalMessageHTML = CommonUtil.removeLeadingTrimmedBreaks(originalMessageHTML);
        originalMessageHTML = "<blockquote> <p>" + CommonUtil.removeTrailingTrimmedBreaks(originalMessageHTML) + "</p> </blockquote>";
      }

      this.logger.info("[MessageComponent][CHANGESPROCESSED] originalMessageHTML: |" + originalMessageHTML + "|", this.message.id);
    } else {
      originalMessageHTML = "<blockquote> <p>" + originalMessageHTML + "</p> </blockquote>";
    }

    this.originalMessage = this.sanitizer.bypassSecurityTrustHtml(originalMessageHTML.replace(/&amp;amp;/g, "&").replace(/&amp;/g, "&"));
    origMessageObject = null;
    this.logger.info("[MessageComponent][CHANGESPROCESSED] originalMessage: |" + this.originalMessage + "|", this.message.id);
  }

  getTranslationForSearch(jsonObj: string){
    let translation;
    this.translate.get(jsonObj).pipe(take(1)).subscribe(v => {
      translation = v;
    });
    return translation;
  }

  isEmojiOnly(str) {
    const emojiRegex = /^[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{2B50}\u{2B55}\u{3030}\u{303D}\u{3297}\u{3299}\u{1F004}\u{1F0CF}\u{1F1E6}-\u{1F1FF}\u{1F004}\u{1F18E}\u{1F201}\u{1F21A}\u{1F22F}\u{1F250}\u{1F251}\u{25AA}\u{25AB}\u{25FE}\u{25FD}\u{25FB}\u{25FC}\u{25B6}\u{25C0}\u{27A1}\u{2B05}\u{2B06}\u{2B07}\u{2194}\u{2195}\u{2196}\u{2197}\u{2198}\u{2199}\s\u{FE0F}\u{200D}\u{2640}\u{2642}\u{2695}\u{2696}\u{2697}\u{2699}\u{26A0}\u{26A1}\u{26AA}\u{26AB}\u{26B0}\u{26B1}\u{26C4}\u{26C5}\u{26C8}\u{26CE}\u{26CF}\u{26D1}\u{26D3}\u{26D4}\u{26E9}\u{26EA}\u{26F0}\u{26F1}\u{26F2}\u{26F3}\u{26F4}\u{26F5}\u{26F7}\u{26F8}\u{26F9}\u{26FA}\u{26FD}]+$/u;
    return emojiRegex.test(str);
  }
  private processMessageBody(skipEmoji?: boolean, processOnly?: boolean) {
    // this.logger.info("[MessageComponent][processMessageBody] message", this.isSearchResult, !!this.message.cachedContent, this.message)
    let messageBody = this.message?.body || "";
    try {
      if (this.message.htmlBody) {
        messageBody = this.message.htmlBody;
      } else if (this.message.html && this.message.html.body) {
        messageBody = this.message.html.body;
      }
      if (!!messageBody) {
        messageBody = messageBody.trim();
        if (/^:[a-zA-Z0-9-_+]+:$/g.test(messageBody) || this.isEmojiOnly(messageBody)) {
          this.emojiOnly = true;
        }
      }
      // this.logger.info("[processMessageBody]", this.message.id, this.message, messageBody, this.emojiOnly);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("[processMessageBody]", error);
    }

    return new Promise(async (resolve) => {
      const references = this.message.references;
      let isTicketMention = false;

      if (messageBody.startsWith("#quickreply_")) {
        const additionalActionsTranslationKeys = ["QUICK_REPLY_CANT_TALK_WHATS_UP", "QUICK_REPLY_CALL_YOU_RIGHT_BACK", "QUICK_REPLY_CALL_YOU_LATER", "QUICK_REPLY_CANT_TALK_CALL_ME_LATER"];
        let additionActionTranslations = {};
        this.translate.get(additionalActionsTranslationKeys).pipe(take(1)).subscribe(v => {
          additionActionTranslations = v;
        });
        // this.logger.info("[processMessageBody_quickReply] ", messageBody, additionActionTranslations);
        try {
          const translateKey = messageBody.split("quickreply_")[1].trim();
          if (additionalActionsTranslationKeys.indexOf(translateKey) > -1) {
            messageBody = additionActionTranslations[translateKey];
            this.message.cachedContent = additionActionTranslations[translateKey];
          }
        } catch (error) {

        }
      }

      if (!this.message.forwardMessage) {
        let translations: any = {};
        this.translate.get(["TICKET_MENTION_RECEIVER_MESSAGE", "TICKET_MENTION_SENDER_MESSAGE", "META_TASK_MENTION_RECEIVER_MESSAGE", "META_TASK_MENTION_SENDER_MESSAGE"]).pipe(take(1)).subscribe(v => {
          translations = v;
        });
        isTicketMention = CommonUtil.parseRedmineMentions(TICKET_MENTION, messageBody).length > 0 || CommonUtil.parseRedmineMentions(META_TASK_MENTION, messageBody).length > 0;
        try {
          if (CommonUtil.parseRedmineMentions(TICKET_MENTION, messageBody).length > 0) {
            const textToReplace = CommonUtil.parseRedmineMentions(TICKET_MENTION, messageBody)[0];
            let userjid = textToReplace.split("#")[1];
            let mentionText = translations.TICKET_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.contactRepo.getFullName(this.message.fromJid));
            if (this.userJID?.bare !== userjid) {
              mentionText = translations.TICKET_MENTION_SENDER_MESSAGE.replace("{{ receiver_name }}", this.contactRepo.getFullName(userjid));
            }
            isTicketMention = true;
            messageBody = messageBody.toString().replace(textToReplace, mentionText).replace(/&lt;/g, "<").replace(/&gt;/g, ">");
            this.isMentionedMessage = true;

          } else if (CommonUtil.parseRedmineMentions(META_TASK_MENTION, messageBody).length > 0) {
            const textToReplace = CommonUtil.parseRedmineMentions(META_TASK_MENTION, messageBody)[0];
            let userjid = textToReplace.split("#")[1];
            let mentionText = translations.META_TASK_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.contactRepo.getFullName(this.message.fromJid));
            if (this.userJID?.bare !== userjid) {
              mentionText = translations.META_TASK_MENTION_SENDER_MESSAGE.replace("{{ receiver_name }}", this.contactRepo.getFullName(userjid));
            }
            isTicketMention = true;
            this.isMentionedMessage = true;
            messageBody = messageBody.toString().replace(textToReplace, mentionText).replace(/&lt;/g, "<").replace(/&gt;/g, ">");
          }
        } catch (error) {

        }
      }

      if (!!this.message.cachedContent && !this.isSearchResult && !skipEmoji && !isTicketMention) {
        if (this.message.cachedContent !== "Encrypted message") {
          // cached contend for decrypted messages is initially "Encrypted message"
          // when decrypt is a bit delayed and the message is shown, the body / htmlBody is
          // updated, but the cahced content is still the same
          messageBody = this.message.cachedContent;
        }
      } else {
        messageBody = CommonUtil.generateCachedBody(messageBody, this.isSearchResult ? this.keyword : null, skipEmoji);
        if (!this.isSearchResult && !skipEmoji) {
          const newMsg = {...this.message, cachedContent: messageBody};

          this.conversationRepo.getSelectedConversation().pipe(filter(res => !!res), take(1)).subscribe(selectedConversation => {
            const convTarget = !!this.message.convTarget ? this.message.convTarget : selectedConversation.Target;
            this.conversationRepo.updateMessage(newMsg, convTarget);
          });

        }
      }

      // mentions
      if (!!messageBody) {
        if (CommonUtil.parseMentions(messageBody).length > 0 || this.message.type === "groupchat" && messageBody.match(/@all\b/ig)) {
          messageBody = this.contactRepo.renderMentionUsers(messageBody, "html", references);
          this.hasMention = true;
          this.changeDetectorRef.markForCheck();
        }
      } else {
        messageBody = "";
      }

      if (this.message.group_action && (this.message.group_action === "ADD_PARTICIPANTS"
      || this.message.group_action.type === "ADD_PARTICIPANTS")) {
        if (this.message.group_action.data) {
          const participants = this.message.group_action.data.split(",");
          messageBody = messageBody.split("<br>")[1];
          if (!messageBody) { // e.g. when add a new user to omemo chat
            messageBody = "";
            participants.forEach((pJid, index) => {
              const pDisplayName = pJid.split("@")[0].replace(".", " ");
              messageBody += pDisplayName;
              if (index !== participants.length - 1) {
                messageBody += ", ";
              }
            });
          }
          this.totalParticipants = participants.length;
        } else {
           const firstLine = messageBody.split("<br>")[0];
           messageBody = messageBody.split("<br>")[1];
           if (!messageBody) { // e.g. when add a new user to omemo chat
             this.totalParticipants = 1;
             messageBody = "";
           } else {
             this.totalParticipants = parseInt(firstLine.toString().replace(/\D+/g, ""));
           }
        }
        this.logger.info("[MessageComponent][ADDN] message: ", this.message);
        this.actorName = this.contactRepo.getFullName(this.fromJid);
        this.translationKey = this.totalParticipants > 1 ? "USER_ADD_N_PARTICIPANTS" : "USER_ADD_N_PARTICIPANT";
      } else if (this.message.group_action && (this.message.group_action === "UPDATE_AVATAR"
      || this.message.group_action.type === "UPDATE_AVATAR")) {
        this.fullActorName = messageBody.toString().replace("has updated the group photo", "").replace("hat das Foto der Gruppe aktualisiert", "");
        this.translationKey = "HAS_UPDATED_GROUP_PHOTO";
      } else if (this.message.group_action && (this.message.group_action === "UPDATE_ENCRYPTION"
        || this.message.group_action.type === "UPDATE_ENCRYPTION" || (!!this.message.group_action.type.type && this.message.group_action.type.type === "UPDATE_ENCRYPTION"))) {
        this.translationKey = "ENABLED_E2E_ENCRYPTION";
          this.encryptionMessage = "enabled";
        if (messageBody.indexOf("disabled E2E encryption for the chat") !== -1) {
          this.translationKey = "DISABLED_E2E_ENCRYPTION";
          this.encryptionMessage = "disabled";
        }
        this.fullActorName = messageBody.toString().replace("enabled E2E encryption for the chat", "").replace("disabled E2E encryption for the chat", "");
      }
       else if (this.message.group_action && (this.message.group_action === "created"
      || this.message.group_action.type === "created")) {
        this.logger.info("groupCreatedCheck: ", this.message);
        if (!!this.message.convTarget){
          if (this.message.convTarget.indexOf("talk_meeting@conference") > -1) {
            this.isMeeting = true;
          }
        }
        this.translationParam = { groupName: messageBody.toString().replace(/Group\s"/, "").replace(/"\sCreated/, "")};
      } else if (!!this.message.group_action && typeof this.message.group_action?.type  === "string" && this.message.group_action?.type?.indexOf("MISSED_CALL") !== -1) {
        try {
          const data = JSON.parse(this.message.group_action?.type);
          this.missCallJids = data.jids;
        } catch (ex) {
          this.logger.sentryErrorLog("parse group_action error", ex);
        }
      } else if (this.message.group_action && this.message.group_action.type === "JOINED_PARTICIPANT") {
        const joinee = this.message.body.split(" ")[0];
        this.fullActorName = this.contactRepo.getFullName(joinee);
        if (joinee === this.userJID?.bare) {
          this.translationKey = "YOU_JOINED_THE_GROUPCHAT";
        } else {
          this.translationKey = "JOINED_THE_GROUPCHAT";
        }
      } else if (this.message.group_action && this.message.group_action.type === "LEAVE_GROUP") {
        this.actorName = this.contactRepo.getFullName(this.fromJid);
        if (this.fromJid === this.userJID?.bare) {
          this.translationKey = "YOU_LEFT_THE_GROUPCHAT";
        } else {
          this.translationKey = "LEFT_THE_GROUPCHAT";
        }
      } else if (this.message.group_action && (this.message.group_action.type === "archived" || this.message.group_action.type === "unarchived")) {
        this.translationParam = {
          groupName: this.message.group_action.data
        };
      }

      const generateSecretKey = "_._" + this.message.to.bare;
      const isMentionedMessageFromTopic = this.message?.body?.startsWith(generateSecretKey);
      if (messageBody && messageBody.includes(TOPIC_MENTION_MESSAGE_RECEIVER) && isMentionedMessageFromTopic) {
        if (this.isMyMessage) {
          const receiverName = this.getFullName.transform(this.message.to.bare);
          const translatedMessage = await this.translate.get("TOPIC_MENTION_MESSAGE_SENDER").pipe(take(1)).toPromise();
          const senderSideMessage = translatedMessage.replace("{{ USERNAME }}", receiverName);
          messageBody = messageBody.toString().replace(TOPIC_MENTION_MESSAGE_RECEIVER, senderSideMessage);
        } else {
          const translatedMessage = await this.translate.get("TOPIC_MENTION_MESSAGE_RECEIVER").pipe(take(1)).toPromise();
          messageBody = messageBody.toString().replace(TOPIC_MENTION_MESSAGE_RECEIVER, translatedMessage);
        }
        messageBody = messageBody.toString().replace(generateSecretKey, "");
      }

      if (this.message.forwardMessage) {
        let translations: any = {};
        this.translate.get(["TICKET_MENTION_RECEIVER_MESSAGE", "TICKET_MENTION_SENDER_MESSAGE", "META_TASK_MENTION_RECEIVER_MESSAGE", "META_TASK_MENTION_SENDER_MESSAGE","TICKET_MENTION_FORWARD_MESSAGE"]).pipe(take(1)).subscribe(v => {
          translations = v;
        });
        try {
          if (CommonUtil.parseRedmineMentions(TICKET_MENTION, messageBody).length > 0) {
            const textToReplace = CommonUtil.parseRedmineMentions(TICKET_MENTION, messageBody)[0];
            let userjid = textToReplace.split("#")[1];
            let mentionText = translations.TICKET_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.contactRepo.getFullName(this.message.forwardMessage.from));
            if (this.userJID?.bare !== userjid) {
              mentionText = translations.TICKET_MENTION_SENDER_MESSAGE.replace("{{ receiver_name }}", this.contactRepo.getFullName(userjid));
            }
            if (this.userJID?.bare === userjid) {
              mentionText = translations.TICKET_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.contactRepo.getFullName(this.message.forwardMessage.from));
            }
            messageBody = messageBody.toString().replace(textToReplace + ": ", "");
            this.isMentionedMessage = true;
          } else if (CommonUtil.parseRedmineMentions(META_TASK_MENTION, messageBody).length > 0) {
            const textToReplace = CommonUtil.parseRedmineMentions(META_TASK_MENTION, messageBody)[0];
            let userjid = textToReplace.split("#")[1];
            let mentionText = translations.META_TASK_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.contactRepo.getFullName(this.message.forwardMessage.from));
            if (this.userJID?.bare !== userjid) {
              mentionText = translations.META_TASK_MENTION_SENDER_MESSAGE.replace("{{ receiver_name }}", this.contactRepo.getFullName(userjid));
            }
            messageBody = messageBody.toString().replace(textToReplace, mentionText);
            this.logger.info("[processMessageBody][forwardMessage] META_TASK_MENTION", messageBody);
          }
        } catch (error) {

        }
        if (!processOnly) {
          if ((messageBody.indexOf("<br />") > -1) || (messageBody.indexOf("<br>") > -1) || (messageBody.indexOf("<p>") > -1)) {
            messageBody = CommonUtil.removeLeadingTrimmedBreaks(messageBody);
            messageBody = CommonUtil.removeTrailingTrimmedBreaks(messageBody);
          }

          this.messageBody = this.sanitizer.bypassSecurityTrustHtml(messageBody.toString().replace(/<a href=/g, "<a target=\"_blank\" class=\"open-new-window\" href=")
          .replace(/<a class="([a-z\s0-9]*)"\shref=/g, "<a target=\"_blank\" class=\"open-new-window\" href="));
        }
      }

      if (messageBody.includes("http://") || messageBody.includes("https://")) {
        this.checkForChannelEntities(messageBody);
      }
      if (!skipEmoji) {
        if ((messageBody.indexOf("<br />") > -1) || (messageBody.indexOf("<br>") > -1) || (messageBody.indexOf("<p>") > -1)) {
          messageBody = CommonUtil.removeLeadingTrimmedBreaks(messageBody);
          messageBody = CommonUtil.removeTrailingTrimmedBreaks(messageBody);
        }

        this.messageBody = this.sanitizer.bypassSecurityTrustHtml(messageBody.replace(/<a href=/g, "<a target=\"_blank\" class=\"open-new-window\" href=")
        .replace(/<a class="([a-z\s0-9]*)"\shref=/g, "<a target=\"_blank\" class=\"open-new-window\" href="));
        this.changeDetectorRef.markForCheck();
      }
      if ((messageBody.indexOf("<br />") > -1) || (messageBody.indexOf("<br>") > -1) || (messageBody.indexOf("<p>") > -1)) {
        messageBody = CommonUtil.removeLeadingTrimmedBreaks(messageBody);
        messageBody = CommonUtil.removeTrailingTrimmedBreaks(messageBody);
      }

      // this.logger.info("[MessageComponent][CHANGESPROCESSED] message: ", this.message, "|" + messageBody + "|");
      this.changeDetectorRef.markForCheck();
      let element = document.createElement("div");
      element.innerHTML = messageBody.toString().replace(/<br(\s\/)?>/ig, "\n");
      this.convRepo.contentForClipBoard[this.message.id] = element.innerText;
      this.cachedMessage = element.innerText;
      element = null;
      resolve(messageBody);
    });

  }

  checkForChannelEntities(msgBody: string) {
    const urls = msgBody.split("http");
    const firstUrl = urls[1] || "";
    const urlTokens = firstUrl.split("/");
    if (firstUrl.includes(ChannelEntityType.COMMENT) && firstUrl.includes(ChannelEntityType.TOPIC) && firstUrl.includes(ChannelEntityType.CHANNEL)) {
      const commentId = this.getCommentId(urlTokens, true);
      if (commentId) {
        this.fetchEntity(ChannelEntityType.COMMENT, commentId);
      }
    } else if (firstUrl.includes(ChannelEntityType.TOPIC) && firstUrl.includes(ChannelEntityType.CHANNEL)) {
      const topicId = this.getTopicId(urlTokens, true);
      if (topicId) {
        this.fetchEntity(ChannelEntityType.TOPIC, topicId);
      }
    } else if (firstUrl.includes(ChannelEntityType.SOCIAL)) {
      const socialPostId = this.getSocialId(urlTokens, true);
      if (socialPostId) {
        this.fetchEntity(ChannelEntityType.SOCIAL, socialPostId);
      }
    } else if (firstUrl.includes(ChannelEntityType.CHANNEL)) {
      const channelId = this.getChannelId(urlTokens, true);
      if (channelId) {
        this.fetchEntity(ChannelEntityType.CHANNEL, channelId);
      }
    }
  }

  private getChannelId(urlTokens, checkForEnd = false) {
    const indexOfChannelKey = urlTokens.indexOf(ChannelEntityType.CHANNEL);
    const id = urlTokens?.[indexOfChannelKey + 1];
    return checkForEnd ? id?.split("\"")?.[0] : id;
  }

  private getTopicId(urlTokens, checkForEnd = false) {
    const indexOfTopicKey = urlTokens.indexOf(ChannelEntityType.TOPIC);
    const id = urlTokens?.[indexOfTopicKey + 1].split("?")?.[0];
    return checkForEnd ? id?.split("\"")?.[0] : id;
  }

  private getSocialId(urlTokens, checkForEnd = false) {
    const indexOfTopicKey = urlTokens.indexOf("status");
    const id = urlTokens?.[indexOfTopicKey + 1].split("?")?.[0];
    return checkForEnd ? id?.split("\"")?.[0] : id;
  }

  private getCommentId(urlTokens, checkForEnd = false) {
    const indexOfTopicKey = urlTokens.indexOf(ChannelEntityType.TOPIC);
    const id = urlTokens?.[indexOfTopicKey + 1].split("?")?.[1]?.split("=")?.[1];
    return checkForEnd ? id?.split("\"")?.[0] : id;
  }

  private fetchEntity(entityType, entityId) {
    if (entityType === ChannelEntityType.COMMENT) {
      this.channelRepository.selectCommentById(entityId).pipe(takeUntil(this.isAlive$))
        .subscribe(async (res: any) => {
          if (res?.notFound || res?.noAccess) { return; }
          const preview = (await this.getMappedData({previewType: ChatPreviewType.COMMENT, resultType: SmartLinkSearchResultType.COMMENT, resultData: {...res, resultType: SmartLinkSearchResultType.COMMENT}})).getValue();
          this.channelEntityPreviewType$.next(preview.previewType);
          this.channelEntityPreviewData$.next(preview.previewData);
          let temp = this.channelEntityPreviewData$.getValue();
          if (temp.channelFullData && temp.channelFullData.description) {
            temp.channelFullData.description = temp.channelFullData.description.slice(0,40) + "...";
          }
          this.channelEntityPreviewData$.next(temp);
        });
    }
    if (entityType === ChannelEntityType.TOPIC) {
      this.channelRepository.selectTopicDetail(entityId).pipe(takeUntil(this.isAlive$))
        .subscribe(async (res: any) => {
          if (res?.notFound || res?.noAccess) { return; }
          let heroAttachments = this.channelRepository.generateHeaderAttachments(res);
          heroAttachments = this.channelRepository.generateLocalAttachmentsURLs(heroAttachments, res?.is_iom);
          const preview = (await this.getMappedData({previewType: ChatPreviewType.TOPIC, resultType: SmartLinkSearchResultType.TOPIC, resultData: {...res, resultType: SmartLinkSearchResultType.TOPIC, heroAttachments: heroAttachments }})).getValue();
          this.channelEntityPreviewType$.next(preview.previewType);
          this.channelEntityPreviewData$.next({topicData: {...res, resultType: SmartLinkSearchResultType.TOPIC, heroAttachments: heroAttachments }});
        });
    }
    if (entityType === ChannelEntityType.SOCIAL) {
      this.socialRepository.getPostById(entityId).pipe(takeUntil(this.isAlive$))
        .subscribe(async res => {
          if (!!res) {
            let heroAttachments = res.attachments;
            heroAttachments = this.socialRepository.generateLocalAttachmentsURLForSocial(heroAttachments);
            let prefixContent = "";
            if (!!res.parent_post) {
              this.translate.get("REPLYING_TO").pipe(take(1)).subscribe((text: string) => {
                this.replyToText = text;
                this.changeDetectorRef.markForCheck();
              });
              prefixContent = `<p><span class="reply-lable-text">${this.replyToText} </span><span class="reply-lable-id-name">${res?.parent_post?.author?.jid}</span></p>`;
            }
            const preview = (await this.getMappedData({previewType: ChatPreviewType.SOCIAL, resultType: "SOCIAL", resultData: {...res, resultType: "SOCIAL", prefixContent: prefixContent, heroAttachments: heroAttachments }})).getValue();
            this.channelEntityPreviewType$.next(preview.previewType);
            this.channelEntityPreviewData$.next({socialPostData: {...res, resultType: "SOCIAL", prefixContent: prefixContent, heroAttachments: heroAttachments }});
          } else {
            this.logger.error("No Post Found");
          }
         });
    }
    if (entityType === ChannelEntityType.CHANNEL) {
      this.channelRepository.selectChannelDetail(entityId).pipe(takeUntil(this.isAlive$))
        .subscribe(async (res: any) => {
          if (res?.notFound || res?.noAccess) { return; }
          const preview = (await this.getMappedData({previewType: ChatPreviewType.CHANNEL, resultType: SmartLinkSearchResultType.CHANNEL, resultData: {...res, resultType: SmartLinkSearchResultType.CHANNEL}})).getValue();
          this.channelEntityPreviewType$.next(preview.previewType);
          this.channelEntityPreviewData$.next(preview.previewData);
          let temp = this.channelEntityPreviewData$.getValue();
          temp.channelFullData.description = temp.channelFullData.description.slice(0,40) + "...";
          this.channelEntityPreviewData$.next(temp);
        });
    }
  }

  private async getMappedData(smartLinkData: ChatPreviewData | any) {
    const data = new BehaviorSubject<{ previewType: SmartLinkData["previewType"] | any, previewData: any, resultData: any }>({
      previewType: smartLinkData.previewType,
      previewData: smartLinkData?.resultType === "SOCIAL" ? this.socialRepository.getMappedPreviewData(smartLinkData.resultData) : this._smartLinkService.getMappedSmartLinkPreviewData(smartLinkData.resultData),
      resultData: smartLinkData.resultData
    });

    if (smartLinkData?.resultType === SmartLinkSearchResultType.CHANNEL) {
      const { channelFullData } = this._smartLinkService.getMappedSmartLinkPreviewData(smartLinkData.resultData);
      const channelMembersAndTopics = await this.getChannelTopicAndMembers(smartLinkData.resultData.id).pipe(take(1)).toPromise();
      data.next({
        previewType: smartLinkData.previewType,
        previewData: { channelFullData: { ...channelFullData, ...channelMembersAndTopics } },
        resultData: smartLinkData.resultData
      });
    }
    return data;
  }

  private getChannelTopicAndMembers(channelId: Channel["id"]) {
    const $channelMembersOb = this.channelRepository.getMembersOfChannel(channelId);
    return combineLatest([$channelMembersOb])
      .pipe(takeUntil(this.isAlive$))
      .pipe(map((res: any) => {
        const members = res[0];
        return {
          members
        };
      }));
  }

  openEntity() {
    const previewType = this.channelEntityPreviewType$.getValue();
    const previewData = this.channelEntityPreviewData$.getValue();
    if (previewType === ChatPreviewType.COMMENT) {
      const { commentFullData } = previewData;
      const { channel_id, topic_id, id: comment_id } = commentFullData;
      if (commentFullData?.topic_is_social) {
        return;
      } else {
        this.router.navigateByUrl(`talk/channels/${channel_id}/topics/${topic_id}?commentId=${comment_id}`);
      }
    }
    if (previewType === ChatPreviewType.TOPIC) {
      const { topicData } = previewData;
      const { channel_id, id: topic_id } = topicData;
      if (topicData?.is_social) {
        return;
      } else {
        this.router.navigateByUrl(`talk/channels/${channel_id}/topics/${topic_id}`);
      }
    }
    if (previewType === ChatPreviewType.CHANNEL) {
      const { channelFullData } = previewData;
      const { id: channel_id } = channelFullData;
      if (channelFullData?.is_social) {
       return;
      } else  {
        this.router.navigateByUrl(`talk/channels/${channel_id}`);
      }
    }
  }
  commentAction(action: CommentAction) {
    const {commentFullData} = this.channelEntityPreviewData$.getValue();
    if (action === CommentAction.LIKE) {
      this.likeComment(commentFullData?.id, commentFullData?.liked);
      return;
    }
    this.openEntity();
  }

  topicActionClicked(event: { action: string, data: any }) {
    if (event.action === "OPEN_COMMENT_FORM") {
      const { topicData } = this.channelEntityPreviewData$.getValue();
      const { channel_id, id: topic_id, last_comment: comment } = topicData;
      let url = `talk/channels/${channel_id}/topics/${topic_id}`;
      if ( comment.id ) {
        url += `?commentId=${comment.id}`;
      }
      this.router.navigateByUrl(url);
    }
  }

  likeTopic(topicId, liked: boolean) {
    if (!liked) {
      this.channelService.likesTopic(topicId).subscribe((v: any) => {
        this.updateTopicLikeStatusLocally(v);
      });
    }
    else {
      this.channelService.unlikesTopic(topicId).subscribe((v: any) => {
        this.updateTopicLikeStatusLocally(v);
      });
    }
  }

  updateTopicLikeStatusLocally(topic) {
    const {topicData} = this.channelEntityPreviewData$.getValue();
    this.channelEntityPreviewData$.next({topicData: {...topicData, liked: topic?.liked, likes_count: topic?.likes_count}});
  }

  likeComment(commentId, liked: boolean) {
    if (!liked) {
      this.channelService.likeComment(commentId).subscribe((v: any) => {
        if (v.comment) {
          this.updateCommentLikeStatusLocally(v.comment);
        }
      });
    } else {
      this.channelService.unlikeComment(commentId).subscribe((v: any) => {
        if (v.comment) {
          this.updateCommentLikeStatusLocally(v.comment);
        }
      });
    }
  }

  updateCommentLikeStatusLocally(comment) {
    const {commentFullData} = this.channelEntityPreviewData$.getValue();
    this.channelEntityPreviewData$.next({commentFullData: {...commentFullData, liked: comment?.liked, likes_count: comment?.likes_count}});
  }

  fileAction(event: {action: any, data: any}) {
    if (event.action === FileActionType.DOWNLOAD_ALL_FILES) {
      this.downloadFiles(event.data, true);
    }
    if (event.action === FileActionType.DOWNLOAD_FILE) {
      this.downloadFiles([event.data]);
    }
    if (event.action === FileActionType.COPY_FILE_LINK) {
      this.copyFileLink(event.data);
    }
    if (event.action === FileActionType.COPY_FILE_TO_CLIPBOARD || event.action === FileActionType.COPY_ALL_FILES_TO_CLIPBOARD) {
      this.copyFilesToClipboard();
    }
  }

  downloadFiles(files: File[], isBulk = false) {
    if (!isBulk) {
      this.fileActionsService.downloadFile(files[0]);
    } else {
      this.fileActionsService.downloadFilesAsZIP(files, `files.zip`);
    }
  }

  copyFileLink(file: File) {
    this.fileActionsService.copyFileLink(file);
  }

  copyFilesToClipboard() {
    this.toastService.show("UNDER_DEVELOPMENT");
  }


  displayPreview(time: number) {
    try {
      const formatStr = this.lang === "de" ? "dd.MM.yyyy" : "dd.MM.yyyy";
      return format(new Date(time), formatStr, {locale: this.locale});
    } catch (e) {
      return "";
    }
  }

  setDisplayTime(): void {
    if (this.previewOnly) {
      this.displayTime = "";
      return;
    }
    const timestamp = this.message?.timestamp;
    if (!!timestamp) {
      const dateFormat = this.settings.dateFormat || this.lang === "de" ? "dd.MM.yyyy" : "dd/MM/yyyy";
      const today = new Date();
      let dd:any = today.getDate();
      let mm:any = today.getMonth() + 1;
      let yyyy:any = today.getFullYear();
      if (dd < 10) {
        dd = "0" + dd;
      }
      if (mm < 10) {
        mm = "0" + mm;
      }
      let isToDay = [yyyy, mm, dd].join("-");
      this.formatToday = format(new Date(isToDay), dateFormat);
      this.messageDate = format(new Date(timestamp), dateFormat);
      this.formatTimestamp = this.addTimestamp;
      const displayTimestamp = +(this.datetimeService.getDisplayTime(timestamp) + "").padEnd(13, "0");
      const formatStr = this.datetimeService.getFormatString(
        this.lang, this.isMiniChat ? true :
          this.formatToday === this.messageDate ? this.formatTimestamp : !this.formatTimestamp
      );

      this.displayTime = format(new Date(displayTimestamp), formatStr, {locale: this.locale});
      if (this.lang === "de") {
        this.displayTime = this.displayTime  + " Uhr";
      }
      if (this.message && this.message.forwardMessage && this.message.forwardMessage.timestamp) {
        this.setForwardTime(this.message.forwardMessage.timestamp);
      }
    }

    // for debug purpose
    if (SHOW_MESSAGE_HEIGHT_DEBUG_INFO) {
      this.displayTime =  `${this.displayTime} (${this.messageHeight})`;
    }
  }

  private setForwardTime(timestamp: number) {
    this.forwardTime = this.getDisplayTimeText(timestamp);
  }

  getDisplayTimeText(timestamp: number) {
    if (this.previewOnly) {
      return "";
    }
    const displayTimestamp = +(timestamp + "").padEnd(13, "0");

    const timeFormat = this.settings.timeFormat || "p";
    const dateFormat = this.settings.dateFormat || this.lang === "de" ? "dd.MM.yyyy" : "dd/MM/yyyy";

    const formatStr = `${dateFormat} ${timeFormat}`;
    let time = format(new Date(displayTimestamp), formatStr, {locale: this.locale});
    if (this.lang === "de") {
      time += " Uhr";
    }
    return time;
  }

  getCallTime(): string {
    if (this.previewOnly) {
      return "";
    }
    const displayTimestamp = this.datetimeService.getDisplayTime(this.message.timestamp);

    let time = format(displayTimestamp, "HH:mm", {locale: this.locale});
    if (this.lang === "de") {
      time = time  + " Uhr";
    }

    return time;
  }

  getDisplayTime(timestamp): string {
    return DateUtil.getDisplayTime(+timestamp, this.lang, this.datetimeService, this.translate);
  }

  ngOnDestroy(): void {
    if (this.checkPlayable) {
      clearInterval(this.checkPlayable);
    }
    if (this.processURL$) {
      clearTimeout(this.processURL$);
    }
    if (this.checkUploadProgress$) {
      this.checkUploadProgress$.unsubscribe();
    }
    this.mainMenu = null;
    this.videoPlayer = null;
    this.changeDetectorRef.detach();
    this.isAlive$.next(false);
    this.isAlive$.complete();
    if (!this.isMiniChat && !this.previewDialog) {
      this.previewClosed$.next(false);
      this.previewClosed$.complete();
    }
    this.destroy$.next(false);
    this.destroy$.unsubscribe();

  }

  trustURL(url) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  getNickName(jid) {
    if (jid && jid.resource) {
      return jid.resource;
    }
    return jid;
  }

  getJID(jid) {
    if (jid && jid.resource && jid.domain) {
      return jid.resource.replace(/\s+/g, "") + "@" + jid.domain.replace("conference.", "");
    }
    return jid;
  }

  cancelFailedAttachment() {
    this.convRepo.removeFailedMessage(this.message);

    this.convRepo.resetMessages();
    this.notificationService.openSnackBarWithTranslation("MESSAGE_DELETED", {});
  }

  resendFailedAttachment() {
    // now open a file picker to choose a file again
    // (we can't resend the same file cause we do not have a real link to it)
    document.getElementById("attachFile").click();

    // w/o this the above click event is not working on iOS
    setTimeout(() => {
      this.convRepo.removeFailedMessage(this.message);
    }, 100);

    this.convRepo.resetMessages();
  }

  copyURL(id) {
    let input = <HTMLInputElement>document.getElementById("attachment" + id);
    if (environment.isCordova) {
      cordova.plugins.clipboard.copy(input.value);
      this.copiedToClipboard();
      return;
    }
    let isiOSDevice = navigator.userAgent.match(/ipad|iphone/i);
    if (isiOSDevice) {
      let editable = input.contentEditable;
      let readOnly = input.readOnly;

      input.contentEditable = "true";
      input.readOnly = false;

      let range = document.createRange();
      range.selectNodeContents(input);

      let selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      range = null;
      input.setSelectionRange(0, 999999);
      input.contentEditable = editable;
      input.readOnly = readOnly;
    } else {
      input.select();
    }
    input = null;
    document.execCommand("copy");
    this.copiedToClipboard();
  }

  copiedToClipboard() {
    this.translate.get(["COPIED_TO_CLIPBOARD"])
      .pipe(take(1))
      .subscribe(res => {
        if (res?.COPIED_TO_CLIPBOARD) {
          this.vncLibraryService.openSnackBar(res?.COPIED_TO_CLIPBOARD, "checkmark", "", "", 2000, "left", "left").subscribe();
        }
      });
  }

  async showProfile(event, jid) {
    event.stopPropagation();
    if (!jid) {
      jid = this.message?.fromJid;
    }
    this.logger.info("showProfile", event, this.message, this.el.nativeElement.querySelector(".avatar").getBoundingClientRect());
    if (CommonUtil.isOnMobileDevice()) {
      const options = {
        maxWidth: "100vw",
        width: "100vw",
        height: "100vh"
      };
      const { ProfileInfoDialogComponent } = await import(
        "app/shared/components/profile-info-dialog/profile-info-dialog.component");
      this.dialog.open(ProfileInfoDialogComponent, Object.assign({
        backdropClass: "vnctalk-form-backdrop",
        panelClass: "vnctalk-form-panel",
        disableClose: true,
        data: {
            jid: jid
        },
        autoFocus: true
    }, options));
    } else {
      this.store.dispatch(new SelectProfile(jid));
      CommonUtil.showCircularMenu(this.el.nativeElement);
    }

  }

  openMap(location) {
    let latLon = location.lat + "," + location.lng;
    if (typeof device !== "undefined" && device.platform.toUpperCase() === "ANDROID") {
      window.open("geo:" + latLon, "_system");
    } else if (typeof device !== "undefined" && device.platform.toUpperCase() === "IOS") {
      cordova.InAppBrowser.open("http://maps.apple.com/?sll=" + latLon + "&z=100&t=k", "_system", "location=yes");
    } else if (this.electronService.isElectron) {
      this.electronService.openExternalUrl("http://maps.google.com/?q=" + latLon);
    } else {
      window.open("http://maps.google.com/?q=" + latLon);
    }
  }

  private setUnfurledData(data: any) {
    this.messageUrlPreviewData = {background: data.background, title: data.title, description: data.description};
    if (data?.image?.url === "https://vncidp.vnc.biz/module.php/themeSURFnet/logo.png") this.invalidPreviewUrlLogo = true;
    this.setIsInsecureContentForUrlPreview();

    // store to db & redux

    this.conversationRepo.getSelectedConversation().pipe(filter(res => !!res), take(1)).subscribe(selectedConversation => {
      const convTarget = !!this.message.convTarget ? this.message.convTarget : selectedConversation.Target;
      this.conversationRepo.storeUrlUnfurledData(this.message, convTarget);
      this.changeDetectorRef.markForCheck();
    });
  }

  private setRedmineData(data: any, isUrlNotAvailable?: boolean) {
    this.messageRedminePreview = {...this.messageRedminePreview, ...data};
    this.logger.info("[MessageComponent][setRedmineData]", data, this.messageRedminePreview, this.message);

    if (!isUrlNotAvailable) {
      this.issueId = this.messageRedminePreview.id;
      this.dueDate = this.messageRedminePreview.due_date;
    } else {
      this.isRedmineUrl = false;
    }

    // store to db & redux
    this.message.redminePreview = this.messageRedminePreview;

    this.conversationRepo.getSelectedConversation().pipe(filter(res => !!res), take(1)).subscribe(selectedConversation => {
      const convTarget = !!this.message.convTarget ? this.message.convTarget : selectedConversation.Target;
      // for debugging / testing #https://vncproject.vnc.biz/issues/30003052-25435 - uncomment this line and filter console for "setRedmineData"
      // this.logger.info("[MessageComponent][setRedmineData]", data, this.messageRedminePreview, this.message, selectedConversation, convTarget);
      this.conversationRepo.storeRedmineData(this.message, convTarget);
      setTimeout(() => {
        if (this.message?.redminePreview) {
          this.conversationRepo.resetRedminePreviewFullData$.next(this.message?.redminePreview);
        }
      }, 500);
      this.changeDetectorRef.markForCheck();
    });
  }

  private setRedmineVersionData(data: any, isUrlNotAvailable?: boolean) {
    if (data.status) {
      data.statusKey = data.status.toUpperCase();
    }
    this.messageRedmineVersionPreview = {...this.messageRedmineVersionPreview, ...data};
    this.logger.info("[MessageComponent][setRedmineVersionData]", data, this.messageRedmineVersionPreview);

    if (!isUrlNotAvailable) {
      this.dueDate = this.messageRedmineVersionPreview.due_date;
    } else {
      this.isRedmineUrl = false;
    }

    // store to db & redux
    this.message.versionPreview = this.messageRedmineVersionPreview;

    this.conversationRepo.getSelectedConversation().pipe(filter(res => !!res), take(1)).subscribe(selectedConversation => {
      const convTarget = !!this.message.convTarget ? this.message.convTarget : selectedConversation.Target;
      this.conversationRepo.storeRedmineVersionData(this.message, convTarget);
      this.changeDetectorRef.markForCheck();
    });
  }

  private setIsInsecureContentForUrlPreview() {
    if (this.hideInsecureContent && !!this.messageUrlPreviewData && this.messageUrlPreviewData.background
      && (!this.messageUrlPreviewData.background.startsWith("url(https")
      && !this.messageUrlPreviewData.background.startsWith("url('https"))) {
      this.isInsecureContent = true;
    } else {
      this.isInsecureContent = false;
    }
  }


  //
  /// Redmine preview
  //

  private getRemineTicketEditableRights(issueId: string): Observable<any> {
    const response = new Subject<any>();

    this.logger.info("[MessageComponent][checkPreviewEditable]", issueId);

    this.isGettingRemineTicketEditableRights = true;
    this.changeDetectorRef.markForCheck();

    this.conversationRepo.checkPreviewEditable(issueId).subscribe(res => {
              this.isGettingRemineTicketEditableRights = false;
        this.logger.info("[MessageComponent][checkPreviewEditable]", res);

        if (res && res.issue) {
          this.issue = res.issue;
          this.mentionableUsers = this.issue.mention.mentionable_users.map(v => {
            return {
              label: v.name,
              value: v.login
            };
          });
        } else {
          this.issue = {};
        }

        response.next(res);
    });

    return response.asObservable().pipe(take(1));
  }


  //
  // change priority

  canEditPriority() {
    if (this.issue) {
      return this.issue.priority?.can_change === "true";
    } else {
      return true;
    }
  }

  canEditProjectVersion() {
    if (this.issue) {
      return this.issue.versions?.can_change_version === "true";
    } else {
      return true;
    }
  }

  openChangeProjectVersionForm() {
    if (!this.issue) {
      if (!this.isGettingRemineTicketEditableRights) {
        if (!this.issueId) {
          this.issueId = this.messageRedminePreview?.id;
        }
        this.getRemineTicketEditableRights(this.issueId).subscribe(() => {
          if (this.canEditProjectVersion()) {
            setTimeout(() => {
              this.openChangeProjectVersionForm();
            });
          } else {
            this.showNoRightsToast();
          }

          this.changeDetectorRef.markForCheck();
        });
      }
    } else {
      this.projectVersionSelect.toggle();
    }
  }

  canEditAssignee() {
    if (this.issue) {
      return this.issue.mention?.can_mention === "true";
    } else {
      return true;
    }
  }

  openChangeAssigneeForm() {
    if (!this.issue) {
      if (!this.isGettingRemineTicketEditableRights) {
        if (!this.issueId) {
          this.issueId = this.messageRedminePreview?.id;
        }
        this.getRemineTicketEditableRights(this.issueId).subscribe(() => {
          if (this.canEditProjectVersion()) {
            setTimeout(() => {
              this.openChangeAssigneeForm();
            });
          } else {
            this.showNoRightsToast();
          }

          this.changeDetectorRef.markForCheck();
        });
      }
    } else {
      this.assigneeSelect.toggle();
    }
  }

  openChangePriorityForm() {
    if (!this.issue) {
      if (!this.isGettingRemineTicketEditableRights) {
        if (!this.issueId) {
          this.issueId = this.messageRedminePreview?.id;
        }
        this.getRemineTicketEditableRights(this.issueId).subscribe(() => {
          if (this.canEditPriority()) {
            setTimeout(() => {
              this.openChangePriorityForm();
            });
          } else {
            this.showNoRightsToast();
          }

          this.changeDetectorRef.markForCheck();
        });
      }
    } else {
      this.redmineService.ticketPriorities().subscribe(priorities => {
        this.priorityOptions = priorities?.issue_priorities;
        this.changeDetectorRef.detectChanges();
        this.prioritySelect.toggle();
      });
    }
  }


  changePriority(priority) {
    this.updateIssues({priority_id: priority.id, lock_version: this.issue.lock_version });
    this.resetRedminePreview();

  }

  resetRedminePreview() {
    this.sendSignal(this.message.id);
  }

  changeProject(project) {
    this.updateIssues({project_id: project.id, lock_version: this.issue.lock_version });
  }

  changeProjectVersion(version) {
    this.updateIssues({fixed_version_id: version.id, lock_version: this.issue.lock_version });
  }

  changeAssignne(user) {
    this.updateIssues({assigned_to_id: user.id, lock_version: this.issue.lock_version });
  }

  //
  // change status

  canEditStatus() {
    if (this.issue) {
      return this.issue.status?.can_change === "true";
    } else {
      return true;
    }
  }

  openStatusChangeForm() {
    this.logger.info("[MessageComponent][openStatusChangeForm]");
    if (!this.issue) {
      if (!this.isGettingRemineTicketEditableRights) {
        if (!this.issueId) {
          this.issueId = this.messageRedminePreview?.id;
        }
        this.getRemineTicketEditableRights(this.issueId).subscribe(() => {
          if (this.canEditStatus()) {
            setTimeout(() => {
              this.openStatusChangeForm();
            });
          } else {
            this.showNoRightsToast();
          }

          this.changeDetectorRef.markForCheck();
        });
      }
    } else {
      this.logger.info("[MessageComponent][openStatusChangeForm] statusSelect.toggle");
      this.statusSelect.toggle();
    }
  }

  changeStatus(status) {
    this.updateIssues({status_id: status.id, lock_version: this.issue.lock_version });
  }


  //
  // change Due date

  canEditDueDate() {
    if (this.issue) {
      return this.issue.due_date?.can_change === "true";
    } else {
      return true;
    }
  }

  openCalendar() {
    if (this.canEditDueDate()) {
      this.dueDatePicker.open();
    }
  }

  onDateChange(event: any) {
    const newDate = format(new Date(event.value), "yyyy-MM-dd");

    if (!this.issue) {
      if (!this.isGettingRemineTicketEditableRights) {
        if (!this.issueId) {
          this.issueId = this.messageRedminePreview?.id;
        }
        this.getRemineTicketEditableRights(this.issueId).subscribe(() => {
          if (this.canEditDueDate()) {
            this.updateIssues({due_date: newDate, lock_version: this.issue.lock_version });
          } else {
            this.showNoRightsToast();
          }

          this.changeDetectorRef.markForCheck();
        });
      }
    } else {
      this.updateIssues({due_date: newDate, lock_version: this.issue.lock_version });
    }
  }


  //
  // change OTA

  canEditOTA() {
    if (this.issue) {
      return this.issue?.ota?.can_change === "true";
    } else {
      return true;
    }
  }

  showOTAForm() {
    this.logger.info("[MessageComponent][showOTAForm]");

    if (!this.issue) {
      if (!this.isGettingRemineTicketEditableRights) {
        if (!this.issueId) {
          this.issueId = this.messageRedminePreview?.id;
        }
        this.getRemineTicketEditableRights(this.issueId).subscribe(() => {
          if (this.canEditOTA()) {
            this.showOTAForm();
          } else {
            this.showNoRightsToast();
          }

          this.changeDetectorRef.markForCheck();
        });
      }
    } else {
      this.isOTAFormDisplayed = this.issue.ota?.can_change === "true";
    }
  }

  closeOTAForm() {
    this.isOTAFormDisplayed = false;
    this.changeDetectorRef.markForCheck();
  }

  updateOta() {
    this.isOTAFormDisplayed = false;
    this.updateIssues({ota: this.messageRedminePreview.ota, lock_version: this.issue.lock_version });
  }


  //
  // change ETA

  canEditEta() {
    if (this.issue) {
      return this.issue.estimated_hours?.can_change === "true";
    } else {
      return true;
    }
  }

  showETAForm() {
    this.logger.info("[MessageComponent][showETAForm]");
    if (!this.issue) {
      if (!this.isGettingRemineTicketEditableRights) {
        if (!this.issueId) {
          this.issueId = this.messageRedminePreview?.id;
        }
        this.getRemineTicketEditableRights(this.issueId).subscribe(() => {
          if (this.canEditEta()) {
            this.showETAForm();
          } else {
            this.showNoRightsToast();
          }

          this.changeDetectorRef.markForCheck();
        });
      }
    } else {
      this.isETAFormDisplayed = this.issue.estimated_hours?.can_change === "true";
    }
  }

  updateETA() {
    this.isETAFormDisplayed = false;
    this.updateIssues({estimated_hours: this.messageRedminePreview.estimated_hours, lock_version: this.issue.lock_version });
  }

  closeETAForm() {
    this.isETAFormDisplayed = false;
    this.changeDetectorRef.markForCheck();
  }

  //
  // Comments

  canAddComment() {
    if (this.issue) {
      return this.issue.mention?.can_mention === "true";
    } else {
      return true;
    }
  }

  addComment() {
    this.updateIssues({notes: this.commentText, lock_version: this.issue ? this.issue.lock_version : 1 });
    this.isCommentFormDisplayed = false;
    this.commentText = "";
    this.changeDetectorRef.markForCheck();
  }

  openCommentForm() {
    this.logger.info("[MessageComponent][openCommentForm]");

    if (!this.issue) {
      if (!this.isGettingRemineTicketEditableRights) {
        if (!this.issueId) {
          this.issueId = this.messageRedminePreview?.id;
        }
        this.getRemineTicketEditableRights(this.issueId).subscribe(() => {
          if (this.canAddComment()) {
            this.openCommentForm();
          } else {
            this.showNoRightsToast();
          }

          this.changeDetectorRef.markForCheck();
        });
      }
    } else {
      this.isCommentFormDisplayed = true;
      this.commentText = "";
      this.changeDetectorRef.markForCheck();

      this.broadcaster.broadcast("messageOpenCommentForm", this.message.id);

      setTimeout(() => {
        if (this.el.nativeElement.querySelector(".commentText")) {
          this.el.nativeElement.querySelector(".commentText").focus();
        }
      }, 500);
    }
  }

  //

  updateIssues(data) {
    const redmineData = {id :this.messageRedminePreview.id };
    this.logger.info("[MessageComponent][updateIssues]", data, this.issueId, this.messageRedminePreview);

    this.isUpdatingTicket = true;

    this.conversationRepo.updateIssues(this.issueId, data).subscribe(res => {
      this.isUpdatingTicket = false;

      if (res.error) {
        if (Array.isArray(res.error)) {
         this.toastService.showSnackbar(res.error[0]);
        } else {
          this.toastService.showSnackbar(res.error);
        }
      } else {
        this.setRedmineData(res.issue);

        this.issue.lock_version = res.issue.lock_version;
        this.resetRedminePreview();

        this.conversationRepo.resetRedminePreview$.next(redmineData);
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  private showNoRightsToast() {
    this.notificationService.openSnackBarWithTranslation("NOT_ALLOWED", {});
  }


  //
  //

  private _isMyMessage() {
    const isM = this.userJID && this.fromJid === this.userJID?.bare;
    // this.logger.info("[MessageComponent][_isMyMessage]", isM, this.fromJid, this.userJID?.bare, this.message.body, this.message);
    return isM;
  }

// returns true if this message is a placeholder message for file upload
  isPlaceholderMessage() {
    return this.message.attachment && this.isPending();
  }

  downloadFileAttachment() {
    if (!this.message.attachment || !this.message.attachment.url) {
      return;
    }
    let fileUrl = this.translatedUrl;
    let mimeType = CommonUtil.getMimeType(fileUrl);
    this.logger.info("[MessageComponent][downloadFileAttachment] fileUrl", mimeType, fileUrl);
    // iOS/Android
    if (environment.isCordova) {

      let headers = [];
      // we need this for HIN only ('if (environment.theme === "hin")'),
      // but it also works with a standard deployment, so we decided to keep it universal.
      headers.push(
        {
            Key: "Authorization",
            Value: localStorage.getItem("token")
        }
      );

      this.filesStorageService.downloadAndSaveFileInBackground(fileUrl, headers, CommonUtil.isOnIOS()).subscribe((localFileUrl) => {
        this.logger.info("[MessageComponent][downloadInBackgroundFileAsBlob] success, localFileUrl: ", localFileUrl);

        if (CommonUtil.isOnAndroid()) {
          this.translate.get("FILE_DOWNLOADED").pipe(take(1)).subscribe(text => {
            alert(text);
          });
        } else {
          this.openLocalFile(localFileUrl);
        }
      }, err => {
        this.logger.error("[MessageComponent][downloadInBackgroundFileAsBlob] err", err);
        this.toastService.show("SOME_UNKNOWN_ERROR");
      });

      this.toastService.show("FILE_STARTED_DOWNLOADING");

    // Electron
    } else if (environment.isElectron) {
      if (CommonUtil.isVideo(this.message.attachment.fileType)) {
        fileUrl = `${fileUrl}?d=true`;
      }
    // ElectronService.downloadFile(this.message.attachment.url, "");
      this.logger.info("[MessageComponent][downloadFileAttachment] electron", fileUrl);
      this.electronService.fileDownloader(fileUrl).pipe(take(1)).subscribe(() => {
        this.translate.get("FILE_DOWNLOADED").pipe(take(1))
          .subscribe(text => {
            this.notificationsService.html("", text, "notify", {
              timeOut: 2000
            });
            this.changeDetectorRef.markForCheck();
          });
      }, (error) => {
        this.logger.info("[PreviewImageDialogComponent][downloadFile] error: ", error);
      });
    } else {
      // window.open(fileUrl + "?d=true", "_blank");
      let messageDetails = {
        messageId: this.message.id,
        fileSize: this.message.attachment.fileSize,
        fileName: this.message.attachment.fileName,
        loaded: 0,
        total: parseInt(this.message.attachment.fileSize),
        url: fileUrl,
      };
      this.convRepo.downloadFile(messageDetails);
    }
  }

  private openLocalFile(localFileUrl: string) {
    let mimeType = CommonUtil.getMediaType(localFileUrl);
    this.logger.info("[MessageComponent][openLocalFile] localFileUrl: " + localFileUrl + ", mimeType: " + mimeType);

    if (CommonUtil.isOnAndroid()) {
      // this plugin is only for Android
      cordova.plugins.FileOpener.canOpenFile(localFileUrl, () => {
        cordova.plugins.FileOpener.openFile(localFileUrl, () => {
          this.logger.info("[MessageComponent][openLocalFile] success");
        }, (error) => {
          this.logger.error("[MessageComponent][openLocalFile] openFile error", error);
          this.toastService.show("SOME_UNKNOWN_ERROR");
        });
      }, (error) => {
        this.logger.error("[MessageComponent][downloadFileAttachment] canOpenFile error", error);
        this.toastService.show("FILE_DOWNLOADED");
      });

    // iOS
    } else {
      this.logger.info("[MessageComponent][downloadFileAttachment] showOpenWithDialog", localFileUrl, mimeType);
      if (CommonUtil.isOnIpad()) {
        mimeType = CommonUtil.getMimeType(localFileUrl);
        this.logger.info("[MessageComponent][downloadFileAttachment] showOpenWithDialog", localFileUrl, mimeType);
        cordova.plugins.fileOpener2.open(
          localFileUrl,
          mimeType,
          {
              error : (e)  => {
                  this.logger.error("[MessageComponent][downloadFileAttachment] Error status: " + e.status + " - Error message: " + e.message);
              },
              success : () => {
                  this.logger.info("[MessageComponent][downloadFileAttachment] file opened successfully");
              }
          }
      );
      } else {
        cordova.plugins.fileOpener2.showOpenWithDialog(
          localFileUrl,
          mimeType,
          {
              error : (e)  => {
                  this.logger.error("[MessageComponent][downloadFileAttachment] Error status: " + e.status + " - Error message: " + e.message);
              },
              success : () => {
                  this.logger.info("[MessageComponent][downloadFileAttachment] file opened successfully");
              }
          }
        );
      }

    }
  }

  DownloaderError(err) {
    this.logger.info("download error: " + err);
    alert("download error: " + err);
  }

  DownloaderSuccess() {
    this.logger.info("download succes");
    let filePath2 = localStorage.getItem("dfp");

    let target = "_system";
    /* if (typeof device !== "undefined"
      && device.platform
      && device.platform.toUpperCase() === "ANDROID"
      && (filePath2.endsWith(".jpeg") || this.localFilePath.endsWith(".png") || this.localFilePath.endsWith(".jpg") || this.localFilePath.endsWith(".gif"))) {
      target = "_blank";
    } */

    if (CommonUtil.isOnAndroid() && cordova.plugins && cordova.plugins.FileOpener) {
      let url = cordova.file.externalCacheDirectory + "downloads/" + localStorage.getItem("dfp");
      this.logger.info("[message.component][DownloadSuccess] testing file: ", url);
      cordova.plugins.FileOpener.canOpenFile(url, () => {
        this.logger.info("[message.component][DownloadSuccess] canOpenFile true: ", url);
        cordova.plugins.FileOpener.openFile(url, () => {
          this.logger.info("[message.component][DownloadSuccess] opened file succes: ", url);
          localStorage.removeItem("dfp");
        }, (error) => {
          this.logger.info("[message.component][DownloadSuccess] opened file error: ", error);
          this.toastService.show("SOME_UNKNOWN_ERROR");
          localStorage.removeItem("dfp");
        });
      }, () => {
        this.toastService.show("SOME_UNKNOWN_ERROR");
        localStorage.removeItem("dfp");
        this.logger.info("[message.component][DownloadSuccess] canOpenFile true", url);
      });
    } else {
      cordova.InAppBrowser.open(cordova.file.cacheDirectory + "downloads/" + filePath2, target, "location=no");
    }
  }

  isPending(): boolean {
    const isP = this.messageStatus === MessageStatus.PENDING || this.messageStatus === MessageStatus.SENDING;
    // this.logger.info("[message.component] isPending", isP, this.message.body);
    return isP;
  }

  isSent(): boolean {
    const isS = this.messageStatus === MessageStatus.SENT;
    // this.logger.info("[message.component] isSent", isS, this.message.body);
    return isS;
  }

  isDelivered(): boolean {
    return this.messageStatus === MessageStatus.DELIVERED;
  }

  isFailed(): boolean {
    return this.messageStatus === MessageStatus.FAILED;
  }

  containTextInBody(): boolean {
    return (this.message?.body || "").split("\n").filter(v => !!v).length > 1 || (this.message?.body || "").split(/\s+/g).filter(v => !!v).length > 1;
  }

  shouldShowBody() {
    return !this.message.originalMessage && this.message.body
      && (((!this.message.attachment || (this.message.attachment && this.containTextInBody() && !this.message.body.startsWith("blob:"))) && !this.message.location)
      || this.isSearchResult);
  }

  selectMessage(messageId?: any) {
    this.convRepo.selectMessage(messageId ? messageId : this.message.id);
  }

  replyMessage(): void {
    this.logger.info("[MessageComponent][replyMessage]", this.message);
    this.selectMessage();
    if (this.activeTab === "mention") {
      this.store.dispatch(new SetActiveTab("chat"));

      setTimeout(() => {
        this.convRepo.navigateToConversation(this.message.room);
      }, 100);
      setTimeout(() => {
        this.broadcaster.broadcast("replyMessage", this.message);
        this.logger.info("[MessageComponent][replyMessage] setTimeout", this.message);
      }, 500);
    } else {
      this.broadcaster.broadcast("replyMessage", this.message);
    }
  }

  deleteMessage(): void {
    this.logger.info("[MessageComponent][deleteMessage]", this.message);
    this.broadcaster.broadcast("deleteMessage", this.message);
  }

  async forwardMessage(messageId?: any) {
    if (messageId) {
      this.selectMessage(messageId);
    } else {
      this.selectMessage();
    }
    let dialogStyles = {
      "width": "517px",
      "maxWidth": "80vw",
      "height": "none",
      "maxHeight": "600px",
      "visibility": "visible"
    };
    if (CommonUtil.isMobileSize()) {
      dialogStyles = {
        "width": "100%",
        "maxWidth": "100%",
        "height": "100%",
        "maxHeight": "100%",
        "visibility": "visible"
      };
    }
    const { ForwardDialogComponent } = await import(
      "app/talk/shared/components/dialogs/forward/forward.component");
    this.isForwardOpen = true;
    this.dialog.open(ForwardDialogComponent, {
      backdropClass: "forward-dialog-backdrop",
      panelClass: ["forward-dialog-panel", "preview-doc-dialog-panel"],
      disableClose: false,
      autoFocus: false,
      width: dialogStyles.width,
      maxHeight: dialogStyles.maxHeight,
      maxWidth: dialogStyles.maxWidth,
      height: dialogStyles.height
    }).afterClosed().pipe(take(1)).subscribe((res: any) => {
      this.isForwardOpen = false;
    });

  }

  handleClickOnOriginalMessage() {
    this.logger.info("[handleClickOnOriginalMessage]", this.message?.originalMessage);
    if (this.message?.originalMessage && this.message?.originalMessage.attachment) {
      try {
        const attachment = JSON.parse(this.message?.originalMessage.attachment);
        if (CommonUtil.isImage(attachment.fileType) || CommonUtil.isSupportedVideo(attachment.fileType)) {
          const originalMsg = {...this.message?.originalMessage, attachment: attachment};
          this.previewDocument(originalMsg);
        }
      } catch (error) {

      }

    }
  }

  async copyMessage(skipNotification?: boolean) {
    let element = document.createElement("div");
    let body = "";
    if (this.message.originalMessage) {
      if (this.message.originalMessage.body && !this.message.originalMessage.htmlBody) {
        body = CommonUtil.unEscapeHTMLString(this.message.originalMessage.body.toString().replace(/&/g, "&amp;"));
      } else if (this.message.originalMessage.htmlBody) {
        body = CommonUtil.unEscapeHTMLString(this.message.originalMessage.htmlBody.toString().replace(/&/g, "&amp;"));
      }
      let origMessageObject: OriginalMessage = {
        ...this.message.originalMessage,
        replyMessage: this.message.originalMessage.replyMessage ? this.message.originalMessage.replyMessage.replace(/&/g, "&amp;") : "",
        body: body
      };

      let { repliedMessage, originalMessageHTML } = this.messageService.renderRepliedMessage(origMessageObject, this.message.references, true);
      if (CommonUtil.parseMentions(repliedMessage).length > 0 || CommonUtil.parseMentions(originalMessageHTML).length > 0
      || (this.message.type === "groupchat") && (repliedMessage.match(/@all\b/ig) || originalMessageHTML.match(/@all\b/ig))) {
        repliedMessage = this.contactRepo.renderMentionUsers(repliedMessage, "html", this.message.references, "");
      }

      element.innerHTML = repliedMessage ? repliedMessage.replace("<br />", "\n") : "";
      this.convRepo.contentForClipBoard[this.message.id] = element.innerText;
      if (!skipNotification) {
        CommonUtil.copyToClipboard([element.innerText]);
      }
    } else {
      (async() => {
        const msg = await this.processMessageBody(true, true);
        this.logger.info("[copyMessage]", msg.toString(), this.message);
        element.innerHTML = msg.toString().replace(/<br(\s\/)?>/ig, "\n");
        this.convRepo.contentForClipBoard[this.message.id] = element.innerText;
        if (!!this.message.attachment) {
          this.convRepo.clipboardAttachment = this.message.attachment;
        }
        if (!skipNotification) {
          CommonUtil.copyToClipboard([element.innerText]);
        }
      })();

    }
    if (!skipNotification) {
      this.notificationService.openSnackBarWithTranslation("MESSAGE_COPIED", { total: 1 });
    }
  }

  async previewImage(data?: any) {
    let message = this.message;
    if (data) {
      message = data;
    }
    let dialogStyles: any = {
      "width": "auto",
      "height": "100%",
      "maxHeight": (window.outerHeight - 80) + "px",
      "maxWidth": window.outerWidth + "px",
      "visibility": "visible"
    };
    if (CommonUtil.isMobileSize()) {
      dialogStyles = {
        "width": "100%",
        "height": "100%",
        "maxHeight": "100%",
        "maxWidth": "100%",
        "visibility": "visible"
      };
    }
    if (CommonUtil.isOnIpad()) {
      dialogStyles = {
        "width": "auto",
        "maxWidth": "100%",
        "height": "100%",
        "visibility": "visible",
        "background-color": "transparent",
        "box-shadow": "none"
      };
    }
    const { PreviewImageDialogComponent } = await import(
      "../dialogs/preview-image/preview-image.component");
    this.dialog.open(PreviewImageDialogComponent, Object.assign({
      data: {
        imageUrl: message.attachment.url,
        message: message
      },
      backdropClass: "vnctalk-form-backdrop",
      panelClass: ["vnctalk-form-panel", "preview-image-host-component"],
      disableClose: true,
      autoFocus: true
    }, dialogStyles)).afterClosed().subscribe((data) => {
      this.logger.info("CLOSE PREVIEW", data);

      if (!!document.querySelector(".preview-image-host-component")) {
        this.logger.info("CLOSE PREVIEW REMOVE");
        document.querySelector(".preview-image-host-component").remove();
      }
      if (data?.isDoubleTapped) {
        this.previewDocument(message, true);
      }
    });
  }

  startChat(): void {
    this.store.dispatch(new SetActiveTab("chat"));
    this.convRepo.navigateToConversation(this.message.fromJid);
  }

  startConference(conferenceType: string) {
    if (this.conferenceRepo.getJitsiServiceIsJoined() && this.conferenceRepo.getJitsiConferenceJoinVal()) {
      this.dialog.open(VNCCommonDialogComponent, {
        width: "480px",
        height: "220px",
        maxHeight: "95%",
        backdropClass: "vnctalk-form-backdrop",
        panelClass: "vnctalk-form-panel",
        disableClose: true,
        autoFocus: true,
        data: {
          headerTranslationKey: "ACTIVE_CALL",
          bodyTranslationKey: "LEAVE_CALL_QUESTION",
          actionTranslationKey: "YES",
          cancelTranslationKey: "NO",
        }
      }).afterClosed().pipe(take(1)).subscribe(v => {
        if (!!v) {
          this.conferenceRepo.leaveOldConference();
          setTimeout(() => {
            this._startConference(conferenceType);
          }, 500);
        }
      });
    } else {
      this._startConference(conferenceType);
    }
  }

  _startConference(conferenceType: string) {
    if (!this.appService.isAppOnline) {
      this.toastService.show("OFFLINE_MODE");
      return;
    }

    if (!this.configService.get("jitsiAvailable")) {
      this.toastService.show("CALLS_NOT_AVAILABLE");
      return;
    }

    this.store.dispatch(new SetActiveTab("chat"));
    // this.conferenceRepo.leaveOldConference();

    let currentConversation: Conversation;
    this.convRepo.getSelectedConversation().pipe(filter(res => !!res), take(1)).subscribe(conv => {
      currentConversation = conv;
    });
    if (currentConversation && currentConversation.Target === this.message.fromJid) {
      this.conferenceRepo.startConference(this.message.fromJid, conferenceType);
    } else {
      this.convRepo.navigateToConversation(this.message.fromJid);
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd), take(1))
        .subscribe(() => {
          this.convRepo.getSelectedConversation().pipe(filter(res => !!res), take(1)).subscribe(conv => {
            if (conv && conv.Target === this.message.fromJid) {
              this.conferenceRepo.startConference(this.message.fromJid, conferenceType);
            }
          });
        });
    }
  }

  async emailToAFriend() {
    this.logger.info("[emailToAFriend]");
    let dialogStyles: any = {
      "width": "517px",
      "maxWidth": "80vw",
      "visibility": "visible"
    };
    if (CommonUtil.isMobileSize()) {
      dialogStyles = {
        "width": "100%",
        "height": "100%",
        "maxWidth": "100%",
        "visibility": "visible"
      };
    }
    const { EmailSelectedMessageComponent } = await import(
      "../email-selected-message/email-selected-message.component");
    this.matDialog.open(EmailSelectedMessageComponent, Object.assign({
      data: {message: this.message.body},
      backdropClass: "vnctalk-form-backdrop",
      panelClass: ["vnctalk-form-panel", "vnctalk-send-mail-to-friend"],
      disableClose: true,
      autoFocus: true
    }, dialogStyles));
  }

  addTask() {
    this.logger.info("[addTask]");
  }

  starMessage() {
    if (!this.message?.id) {
      return;
    }
    const messageId = this.message.id.replace(/.*_/ig, "");
    this.convRepo.addMessageFavorite(messageId).pipe(takeUntil(this.isAlive$)).subscribe(res => {
      this.logger.info("[addMessageFavorite]", res);
      this.message.isStarred = true;
      this.onStarChange.emit(this.message);
      this.convRepo.udpateFavouriteEvent$.next(this.message);
      if (this.isSearchResult) {
        this.searchRepo.updateMessages([this.message]);
      }
      this.convRepo.updateMessageFavorite(messageId, true);

      this.conversationRepo.getSelectedConversation().pipe(filter(v => !!v), take(1)).subscribe(selectedConversation => {
        const convTarget = !!this.message.convTarget ? this.message.convTarget : selectedConversation.Target;
        this.databaseService.createOrUpdateMessages([{...this.message, isStarred: true}], convTarget);
        this.changeDetectorRef.markForCheck();
      });
      if (!!this.headerActions) {
        this.headerActions[0].label = this.translations.REMOVE_FROM_FAVORITES;
        this.headerActions[0].vncIconName = "favorites-filled-new";
      }
    });
  }

  unstarMessage() {
    if (!this.message?.id) {
      return;
    }
    const messageId = this.message.id.replace(/.*_/ig, "");
    this.convRepo.removeMessageFavorite(messageId).pipe(takeUntil(this.isAlive$)).subscribe(res => {
      this.logger.info("[removeMessageFavorite]", res);
      this.message.isStarred = false;
      this.onStarChange.emit(this.message);
      this.convRepo.udpateFavouriteEvent$.next(this.message);
      if (this.isSearchResult) {
        this.searchRepo.updateMessages([this.message]);
      }
      this.convRepo.updateMessageFavorite(messageId, false);

      this.conversationRepo.getSelectedConversation().pipe(filter(v => !!v), take(1)).subscribe(selectedConversation => {
        const convTarget = !!this.message.convTarget ? this.message.convTarget : selectedConversation.Target;
        this.databaseService.createOrUpdateMessages([{...this.message, isStarred: false}], convTarget);
        this.changeDetectorRef.markForCheck();
      });
      if (!!this.headerActions) {
        this.headerActions[0].label = this.translations.ADD_TO_FAVORITES;
        this.headerActions[0].vncIconName = "favorites-new";
      }
    });
  }

  checkForChanges() {
    setTimeout(() => {
      this.changeDetectorRef.markForCheck();
    }, 0);
  }

  async underDevelopment() {
    this.toastService.show("UNDER_DEVELOPMENT");

  }

  getFullUrl(url: string) {
    return CommonUtil.getFullUrl(url);
  }

  setPlaceHolder() {
    if (this.isImage) {
      this.isImage = false;
      this.logger.info("[IMG] replace with URL!");
      setTimeout(() => {

        this.changeDetectorRef.markForCheck();
      }, 1000);
    }
    if (CommonUtil.isOnNativeMobileDevice()) {
      this.localUrl = CommonUtil.getFullUrl("/assets/loading-image.gif");
      this.changeDetectorRef.markForCheck();
    }
  }

  openUrl(event: any, url: string): void {
    if (environment.isCordova || this.electronService.isElectron) {
      event.preventDefault();
      event.stopPropagation();
      let serverURL = localStorage.getItem("serverURL");
      this.store.select(getBaseApiUrl).pipe(take(1)).subscribe(v => serverURL = v);
      if ((url.indexOf(`${serverURL}/talk/channels/`) !== -1) || (url.indexOf(`${serverURL}/talk/social/`) !== -1)) {
        const channelUrl = url.split("/talk/")[1];
        this.router.navigateByUrl(`/talk/${channelUrl}`);
        if (CommonUtil.isOnMobileDevice()) {
          setTimeout(() => {
            this.broadcaster.broadcast("showRightPanel");
          }, 100);
        }
        return;
      }
      if (this.electronService.isElectron) {
        this.electronService.openExternalUrl(url);
      } else if (device.platform === "iOS") {
        window.open(url, "_system");
      } else if (device.platform === "Android") {
        navigator.app.loadUrl(url, {
          openExternal: true
        });
      }
    } else {
      const locationURL = window.location.origin;
      if ((url.indexOf(locationURL + "/talk/channels/") !== -1) || (url.indexOf(locationURL + "/talk/social/") !== -1)) {
        event.preventDefault();
        event.stopPropagation();
        const channelUrl = url.split("/talk/")[1];
        this.router.navigateByUrl(`/talk/${channelUrl}`);
        if (CommonUtil.isOnMobileDevice()) {
          setTimeout(() => {
            this.broadcaster.broadcast("showRightPanel");
          }, 100);
        }
        return;
      }
      const newWindow = window.open(url, "_blank");
      newWindow.focus();
    }
  }

  isBroadcastMessage(): boolean {

    let selectedConversation: Conversation;
    this.conversationRepo.getSelectedConversation().pipe(take(1)).subscribe(conv => selectedConversation = conv);
    if (!selectedConversation) {
      return false;
    }
    const isB = this.message.broadcast_owner
    || (this.message.from && this.message.from.local && this.message.from.local.startsWith("broadcast-"))
    || (this.message.to && this.message.to.local && this.message.to.local.startsWith("broadcast-"))
    || (this.message.forwardMessage && this.message.forwardMessage.from && this.message.forwardMessage.from.startsWith("broadcast-"))
    || selectedConversation && selectedConversation.Target.startsWith("broadcast-");

    return isB;
  }

  get isBroadcastOwner(): boolean {

    let selectedConversation: Conversation;
    this.conversationRepo.getSelectedConversation().pipe(take(1)).subscribe(conv => selectedConversation = conv);

    return selectedConversation && this.userJID && selectedConversation.broadcast_owner === this.userJID?.bare;
  }

  jumpToOriginal($event: any) {
    if ($event.target.tagName === "A") {
      this.openUrl($event, $event.target.href);
      $event.stopPropagation();
      $event.preventDefault();
      return;
    }
    if ($event.target.classList.contains("open-new-window")) {
      this.openUrl($event, $event.target.href);
    } else if ( (this.activeTab === "mention" || this.activeFilter === "mention") && document.querySelector(".vnc-chat-window-message-action-header") === null) {
      let selectedConversation: Conversation;
      this.conversationRepo.getSelectedConversation().pipe(filter(res => !!res), take(1)).subscribe(conv => selectedConversation = conv);

      const currentTarget = selectedConversation?.Target;
      const messageId = this.message.id;

      this.logger.info("[MessageComponent][jumpToOriginal] messageId", messageId);
      this.store.dispatch(new SetActiveFilter("all"));
      this.store.dispatch(new SetActiveTab("chat"));
      this.conversationRepo.navigateToConversation(null);

      setTimeout(() => {
        this.conversationRepo.navigateToConversationAndJumpToMessage(currentTarget, this.message);
      });
    }
  }

  reArrangeInnerText(data) {
      let parser = new DOMParser();

      // Parse the HTML string
      let doc = parser.parseFromString(data, "text/html");

      // Select all li elements with a pre tag, strong tag, or both
      let elementsWithPreOrStrong = doc.querySelectorAll("li > pre, li > strong");

      // Iterate through the selected elements
      elementsWithPreOrStrong.forEach(function (element) {
          // Create a new paragraph element
          let paragraph = doc.createElement("p");

          // Clone the element
          let elementClone = element.cloneNode(true);

          // Append the cloned element to the paragraph
          paragraph.appendChild(elementClone);

          if (!!element.parentNode && !!element.parentNode.parentNode) {

            // Insert the new structure after the original li element
            element.parentNode.parentNode.insertBefore(paragraph, element.parentNode.nextSibling);

            // Remove the original li element
            element.parentNode.parentNode.removeChild(element.parentNode);
          }
      });

      // Select all strong tags
      let strongTags = doc.querySelectorAll("strong");

      // Iterate through the selected elements
      strongTags.forEach(function (strongElement) {
          // Create a new paragraph element
          let paragraph = doc.createElement("p");

          // Clone the strong element
          let strongClone = strongElement.cloneNode(true);

          // Append the cloned strong element to the paragraph
          paragraph.appendChild(strongClone);

          // Replace the original strong element with the new structure
          strongElement.parentNode.replaceChild(paragraph, strongElement);
      });

      // Convert the modified HTML back to a string
      let modifiedData = doc.body.innerHTML;
    return modifiedData;
  }
  jumpToMessage() {
    let selectedConversation: Conversation;
    this.conversationRepo.getSelectedConversation().pipe(filter(res => !!res), take(1)).subscribe(conv => selectedConversation = conv);

    const currentTarget = selectedConversation.Target;
    this.logger.info("[jumpToMessage]", this.message);
    this.store.dispatch(new SetActiveTab("chat"));
    this.store.dispatch(new SetActiveFilter("all"));
    this.conversationRepo.navigateToConversation(null);
    setTimeout(() => {
      this.conversationRepo.navigateToConversationAndJumpToMessage(currentTarget, {id: this.message.id, timestamp: this.message.timestamp});

    });
  }

  showDuration(time: string): string {
    if (time.startsWith("00:")) {
      time = time.slice(3, time.length);
    }
    return time;
  }

  renderCallHistory(callHistoryMessages): string {
    if (!callHistoryMessages) {
      return;
    }

    // this.logger.info("[callHistoryMessages]", callHistoryMessages);

    let content = [];
    let callDuration;
    let isAtLeastOneJoinSignal = false;
    let joinCountPerUser = {};
    this.translate.get(["YOU", "USER_HAS_NOT_ANSWERED", "YOU_CALL", "YOU_JOINED", "YOU_REJOINED", "VERB", "STARTED", "JOINED", "REJOINED", "REJECTED", "ENDED", "CALL_ENDED"]).pipe(take(1)).subscribe(text => {

      callHistoryMessages.forEach(m => {

        let joinText = text.VERB;

        // name
        let name;
        if (this.userJID && m.fromJid === this.userJID?.bare) {
          name = text.YOU_CALL;
          joinText = "";
        } else {
          let contact: Contact;
          this.contactRepo.getContactById(m.fromJid).pipe(take(1)).subscribe(c => {
            contact = c;
          });
          //
          if (!!contact) {
            name = contact.name;
          } else {
            name = !!m.fromJid ? m.fromJid.split("@")[0] : "";
          }
        }

        // action
        const eventType = m.vncTalkConference.eventType;
        let action = "";
        switch (eventType) {
          case "join": {

            // track re-joins?
            if (!joinCountPerUser[m.fromJid]) {
              joinCountPerUser[m.fromJid] = 0;
            }
            ++joinCountPerUser[m.fromJid];

            if (this.userJID && m.fromJid === this.userJID?.bare) {
              name = text.YOU;

              if (joinCountPerUser[m.fromJid] === 1) {
                action = text.YOU_JOINED;
              } else {
                action = text.YOU_REJOINED;
              }
            } else {
              if (joinCountPerUser[m.fromJid] === 1) {
                action = text.JOINED;
              } else {
                action = text.REJOINED;
              }
            }

            isAtLeastOneJoinSignal = true;

            break;
          }
          case "leave": action = joinText + " " + text.ENDED; break;
          case "cancel": action = joinText + " " + text.ENDED; break;
          case "reject": action = joinText + " " + text.REJECTED; break;
        }

        // call duration in a call is ended for you
        if (eventType === "leave" || eventType === "cancel") {
          if (this.userJID && m.fromJid === this.userJID?.bare) {
            if (m.vncTalkConference.duration) {
              callDuration = `. ${text.CALL_ENDED} - ${this.showDuration(m.vncTalkConference.duration)}`;
            }
          }
        } else if (eventType === "no-answer") {
          if (this.userJID && m.vncTalkConference.from === this.userJID?.bare) {
            this.noAnswer = text.USER_HAS_NOT_ANSWERED;
            this.callEnded = text.CALL_ENDED;
          }
        }

        // final content
        content.push(`${name} ${action.trim()}`);
      });
      let res = content.join(", ");
      if (callDuration && isAtLeastOneJoinSignal) {
        res += callDuration;
      }

      this.callHistory = res;
      // this.logger.info("[MessageComponent][renderCallHistory]", callHistoryMessages, content, res);

      this.changeDetectorRef.markForCheck();
    });
  }

  onRightClick(event) {
    if (environment.isElectron && environment.enableOauthLogin) {
      event.preventDefault();
    }
  }

  processText(text) {
    if (!text) {
      return "";
    }
    // text = text.replace("<p>", "").replace("</p>", "")
    // if (text.length > 200) {
    //   text = this.smartSubstr(text, 200);
    // }
    const content = document.createElement("div");
    content.innerHTML = textile(text) || "";
    return content.innerHTML.replace(/<a\s+/g, "<a class=\"open-new-window\" target=\"_blank\" ");
  }

  processDescription(text: string) {
    if (!text) {
      return "";
    }
    if (text.length > 300) {
      text = CommonUtil.smartSubstr(text, 300);
    }
    text = this.reArrangeInnerText(text);
    setTimeout(() => {
      this.hideImagesInHTML();
    }, 500);
    return text.replace(/<a\s+/g, "<a class=\"open-new-window\" target=\"_blank\" ");
  }

  hideImagesInHTML() {
    if (this.isMobileScreen) {
      return;
    }
    const element = document.getElementById("text-content" + this.message.id);
    if (element) {
      const images = element.querySelectorAll("img");
      images.forEach(img => {
          img.style.display = "none";
      });
      const redminPreview = document.getElementById("redmine-preview" + this.message.id);
      let redmineWidth = 0;
      if (redminPreview) {
        redmineWidth =  redminPreview.clientWidth;
        images.forEach(img => {
          img.style.maxWidth = redmineWidth - 50 + "px";
          img.style.display = "block";
        });
      }
      this.setRedminePreviewWidth();

    }
}

 setRedminePreviewWidth() {
    let redminMessageEl = document.getElementById("redmine-preview" + this.message.id);
    let redminePreviewWrapperEl = document.getElementById("redmine-preview-wrapper" + this.message.id);
    if (redminMessageEl && redminePreviewWrapperEl) {
        redminePreviewWrapperEl.style.display = "none";

        if (redminMessageEl.clientWidth < 550 ) {
          redminePreviewWrapperEl.style.display = "flex";
          redminePreviewWrapperEl.style.width = "auto";
          redminePreviewWrapperEl.style.maxWidth = "550px";

        } else {
          redminePreviewWrapperEl.style.display = "flex";
        }

    }
 }

  onMentionSelect(item: any) {
    if (item) {
      return `@${item.value} `;
    }
    return "";
  }

  likeItem(socialData: any) {
    if (socialData?.post && socialData.post.liked) {
      this.socialRepository.unlikePost(socialData?.post?.id);
    } else {
      this.socialRepository.likePost(socialData?.post?.id);
    }
  }

  replyItem(post: any): void {
    this.createSocialPost(post);
  }

  async createSocialPost(post?: any, isEdit?: boolean) {
    let options: any = {
      maxWidth: "960px",
      maxHeight: "100vh",
      width: "960px",
      height: "auto"
    };
    const body: any = {};
    if (post) {
      if (isEdit) {
        body.post = post;
      } else {
        body.parentId = post?.id;
      }
    }
    const { CreatePostComponent } = await import(
      "app/social/create-post/create-post.component");
    this.matDialog.open(CreatePostComponent, Object.assign({
      backdropClass: "vnctalk-form-backdrop",
      panelClass: ["vnctalk-form-panel", "vnctalk-create-social-post-panel"],
      disableClose: true,
      data: body,
      autoFocus: false
    }, options));
  }

  shareItem(): void {
    this.socialRepository.underDevelopmemnt();
  }

  repostItem(post: any): void {
    this.socialRepository.rePost(post);
  }

  navigateToProfile(userJid) {
    this.router.navigateByUrl("/talk/social/" + userJid);
  }

  getTimeAgo(date:any) {
    if (typeof date === "string") {
      return formatDistanceToNow(Date.parse(date), {addSuffix: true, locale: this.locale});
    }
    return "";
  }

  isUpdated(createdOn:any, updatedOn:any){
    return isBefore(Date.parse(createdOn), Date.parse(updatedOn));
  }

  async deleteAndCopyMessage() {
    await this.copyMessage(true);
    if (this.convRepo.contentForClipBoard[this.message.id]) {
      this.conversationRepo.getSelectedConversation(this.isMiniChat).pipe(take(1)).subscribe(conv => {
    this.logger.info("[deleteAndCopyMessage]", this.message.id, this.convRepo.contentForClipBoard[this.message.id]);

        if (!!conv) {
          this.store.dispatch(new SetMessageToCopyAndDelete({
            conversationTarget: conv.Target,
            text: this.convRepo.contentForClipBoard[this.message.id],
            messageId: this.message.id
          }));
        }
      });
    }
  }
  get isAIMessage() {
    return this.message.id?.includes("prompt") || this.message.id?.includes("answer");
  }

  get isAIPrompt() {
    return this.message.id?.endsWith("prompt");
  }

  get isAIAnswer() {
    return this.message.id?.endsWith("answer") || this.message.id?.endsWith("answerfinal");
  }

  async saveToOwncloud(file) {
  const { OwncloudUploadDialogComponent } = await import(
    "../dialogs/owncloud-upload/owncloud-upload.component");
    this.dialog.open(OwncloudUploadDialogComponent, {
      width: "480px",
      maxWidth: "95%",
      height: "610px",
      backdropClass: "common-dialog-backdrop",
      panelClass: "common-dialog-panel",
      disableClose: false,
      data: {
        file: file
      },
      autoFocus: true
    }).afterClosed().pipe(take(1)).subscribe((data) => {
      this.logger.info("[saveToOwncloud]", data);
      if (data && data.shared) {

      }
  });
}

  private getSocialTopicId(urlTokens, checkForEnd = false) {
    const indexOfTopicKey = urlTokens ? urlTokens.indexOf(ChannelEntityType.SOCIAL_TOPIC) : -1;
    const id = urlTokens?.[indexOfTopicKey + 1].split("?")?.[0];
    return checkForEnd ? id?.split("\"")?.[0] : id;
  }

  get totalSelectedMessages() {
    let selectedMessages = [];
    if (this.isSearchResult) {
      this.searchRepo.getSelectedMessages().pipe(take(1)).subscribe(messages => {
        selectedMessages = messages;
      });
    } else {
      this.conversationRepo.getSelectedMessages().pipe(take(1)).subscribe(messages => {
        selectedMessages = messages;
      });
    }
    return selectedMessages.length;
  }

  get cannotDecryptMessage() {
    return this.message?.body?.startsWith("Can't decrypt the message") && this.message?.$body?.en === "Encrypted message";
  }

  updateReaction(reactionCode) {
    this.logger.info("[MessageComponent] updateReaction ", this.message, reactionCode);
    const payload = {msgid: this.message.id, target: this.message.convTarget, reaction: reactionCode};
    this.emojiReactionService.addPendingReaction(payload);
  }

  removeReaction(){
    this.updateReaction("");
  }

  onReactionClick(data){
    if (data.action === "add"){
      this.updateReaction(data.emoji);
    } else if (data.action === "remove") {
      this.removeReaction();
    } else {
      /// TODO: add logger here
    }
    // console.log(data)
  }

  cancelUpload() {
    this.conversationRepo.cancelSendFileRequest(this.message.id);
    this.fileUploaded = true;
    this.changeDetectorRef.markForCheck();
    this.conversationRepo.getSelectedConversation(this.isMiniChat).pipe(take(1)).subscribe(conv => {
      this.store.dispatch(new ConversationUpdate({target: conv?.Target, changes: {
        content: ""
      }}));
    });

  }

  isEmojiOnlyData(str :any = this.messageBody) {
    const emojiRegex = /^[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{2B50}\u{2B55}\u{3030}\u{303D}\u{3297}\u{3299}\u{1F004}\u{1F0CF}\u{1F1E6}-\u{1F1FF}\u{1F004}\u{1F18E}\u{1F201}\u{1F21A}\u{1F22F}\u{1F250}\u{1F251}\u{25AA}\u{25AB}\u{25FE}\u{25FD}\u{25FB}\u{25FC}\u{25B6}\u{25C0}\u{27A1}\u{2B05}\u{2B06}\u{2B07}\u{2194}\u{2195}\u{2196}\u{2197}\u{2198}\u{2199}\s\u{FE0F}\u{200D}\u{2640}\u{2642}\u{2695}\u{2696}\u{2697}\u{2699}\u{26A0}\u{26A1}\u{26AA}\u{26AB}\u{26B0}\u{26B1}\u{26C4}\u{26C5}\u{26C8}\u{26CE}\u{26CF}\u{26D1}\u{26D3}\u{26D4}\u{26E9}\u{26EA}\u{26F0}\u{26F1}\u{26F2}\u{26F3}\u{26F4}\u{26F5}\u{26F7}\u{26F8}\u{26F9}\u{26FA}\u{26FD}]+$/u;
    let tempElement = document.createElement("div");
    tempElement.innerHTML = this.messageBody["changingThisBreaksApplicationSecurity"];
    let plainText = tempElement.innerText;
    return emojiRegex.test(plainText);
  }

  cleanBrTags(input) {
    if (typeof input.changingThisBreaksApplicationSecurity !== "string") {
      return input;
    }
    if (!this.isRedmineUrl) {
      return input;
    }

    if (this.isMentionedMessage) {
      input.changingThisBreaksApplicationSecurity = input.changingThisBreaksApplicationSecurity.replace(/(<br\s*\/>\s*){2,}/g, "<br />");
      const msg = document.getElementsByClassName(`message-content-${this.message.id}`);
      if (msg.length > 0){
          msg[0].innerHTML = msg[0].innerHTML.replace(/<br\s*\/?>/gi, `<span class='custom-br'></span>`);
           const ul = `<ul> <span class="custom-br"></span>`;
            const regex = new RegExp(ul, "gi");
            msg[0].innerHTML = msg[0].innerHTML.replace(regex, `<ul>`);

            const liClose = `<span class="custom-br"></span>	</li>`;
            const liCloseRegex = new RegExp(liClose, "gi");
            msg[0].innerHTML = msg[0].innerHTML.replace(liCloseRegex, `</li>`);

          //  msg[0].innerHTML = this.fixHtmlString(msg[0].innerHTML);
          input.changingThisBreaksApplicationSecurity = input.changingThisBreaksApplicationSecurity.replace(/(<br\s*\/>\s*){2,}/g, "<br />");
      }
      this.changeDetectorRef.markForCheck();
    }

    return input;
  }

  fixHtmlString(html) {
    // Remove <br> tags directly inside <ul> and <li> tags
    return html.replace(/(<ul[^>]*>)\s*<br>/g, "$1");
  }

  isMentionedMessage: boolean = false;

  handleClickEvent(event: any) {
    this.logger.info("handleClickEvent", event);
    let url: string;
    if (event.target?.classList?.contains("open-new-window")) {
      url = event.target.href;
    } else if (event?.target?.nodeName !== "A" && event.path && event.path[1] && event.path[1].classList && event.path[1].classList.contains("open-new-window")) {
      url = event.path[1].href;
    }
    if (this.isAppOnline && url && (url.indexOf("vncmeet/join") !== -1 || url.indexOf("assets/meeting/?jwt=") !== -1)) {
      event.stopPropagation();
      event.preventDefault();
      const splitVerb = (url.indexOf("vncmeet/join") !== -1) ? "/vncmeet/join/" : "/assets/meeting/?jwt=";
      const serverURL = url.split(splitVerb)[0];
      const token = url.split(splitVerb)[1];
      const jwtObject = CommonUtil.verifyJWT(token);

      if (!!jwtObject && !!jwtObject.target && !!jwtObject.roomId) {
        if (CommonUtil.isGroupTarged(jwtObject.target)) {
          // this.logger.info("MESSAGECOMPONENTCLICK isGroup => join  ", jwtObject);
          this.conferenceRepo.joinCallFromLink(jwtObject.target, jwtObject.roomId);

        } else {
          // this.logger.info("MESSAGECOMPONENTCLICK noGroup ", jwtObject.target, this.message);
          this.openUrl(event, url);
        }
      } else {
        this.logger.info("MESSAGECOMPONENTCLICK can not open ");
        this.openUrl(event, url);
      }
    }
  }

  get redmineHeight() {
    return this.isMobileSize ? "auto" :
    (this.isCommentFormDisplayed ? {"height": "320px"} : {"height": "258.94"});
  }

  get redmineHeightClass() {
    return this.isMobileSize ? (this.isCommentFormDisplayed ? "mobile-with-comment" : "mobile-without-comment") :
    (this.isCommentFormDisplayed ? "with-comment" : "without-comment");
  }

  copyPrompt() {
    if (this.isAIPrompt) {
      let prompt = this.message.body;
      if (this.message.attachment && this.message.attachment.url) {
        prompt = `${prompt} ${this.message.attachment.url}`;
      }
      this.logger.info("[copyPrompt]", prompt);
      this.broadcaster.broadcast("copyPrompt", prompt);
    }
  }

  cancelRequest() {
    this.logger.info("cancelRequest", this.message);
    const promptId = this.message.id.split("__")[0];
    this.conversationRepo.WS.send(
      JSON.stringify({
        action: "cancelRequest",
        id: this.message.id
      })
    );
    this.broadcaster.broadcast("deleteMessage", {
      id: promptId
    });
    this.deleteMessage();
  }

  getExtention(name) {
    let ext = "." + name.split(".").pop();
    if (name) {
      if (name.length < 70) {
        ext = "";
      }
    }
    return ext;
  }

  getExtentionFromUrl(url) {
    const isFileUrl = url.indexOf("http") !== -1;
    if (isFileUrl) {
      let fileExt = url.split(".")[url.split(".").length - 1];
      if (fileExt) {
        const zipExtensions = ["7z", "zip", "rar", "tar", "gz", "jar", "tgz"];
        if (zipExtensions.includes(fileExt) || CommonUtil.FILE_TYPES_AVAILABLE_FOR_PREVIEW.includes(fileExt) || fileExt?.toLowerCase() === "json") {
          return fileExt;
        } else return "";
      }  else return "";
    } else return "";
  }

  checkuserReaction() {
    this.resetEmojiSelection();
    if (this.message && this.message.reactions && this.userJID?.bare) {
      const result = this.message.reactions.find(obj => obj.hasOwnProperty(this.userJID.bare));
      if (result) {
        this.emojiCollection.map(emoji => {
          if (emoji.code == result[this.userJID.bare]) {
            emoji["selected"] = true;
          }
        });
      }
    }
  }

  resetEmojiSelection() {
    this.emojiCollection.map(emoji => {
      emoji["selected"] = false;
    });
  }

  sharedContactData = {
    name: "",
    phone: "",
    jid: "",
    company: "",
    role: ""
  };
  contactBlob: Blob;
  getSharedContactData() {
    if (this.message && this.message.attachment && this.message.attachment.fileType && this.message.attachment.fileType === "vcf"){
      // console.log("clickMessage message" , this.message);
      fetch(this.message.attachment.url)
      .then(response => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.text();
      })
      .then(vcfData => {
          this.contactBlob = new Blob([vcfData], { type: "text/vcard" });
          let p = this.parseVcf(vcfData);
          if (!!p && !!p.jid) {
            this.contactRepo.getContactById(p.jid).subscribe(contact => {
              if (!!contact) {
                this.isAlreadyExistingContact = true;
                this.changeDetectorRef.markForCheck();
              }
            });
          }
          this.changeDetectorRef.markForCheck();

      })
      .catch(error => {
      });
    }

  }

  parseVcf(fileContent) {
    const vcfLines = fileContent.split("\n");
    let currentContact: any = {};
    let vcard: any;
    try {
      vcard = vCard.parse(fileContent);
      this.logger.info("vcardparsed parser: ", vcard, vcfLines);
      const firstName = vcard.n[0]?.value[1]?.trim();
      const lastName = vcard.n[0]?.value[0]?.trim();
      const fullName = vcard.fn[0]?.value?.trim();
      const email = vcard.email ? vcard.email[0]?.value : "";
      const ojid = vcard.jid ? vcard.jid[0]?.value : "";
      const jid = (!!ojid && ojid !== "") ? ojid : email;
      const company = vcard.org ? vcard.org[0].value : "";
      const role = vcard.title ? vcard.title[0].value : "";
      currentContact = {
        name: fullName,
        email: email,
        jid: jid,
        company: company,
        role: role
      };

    } catch (error) {
      this.logger.error("vcardparse error: ", error);
    }
    this.sharedContactData = currentContact;
    this.logger.info("vcardparsed: ", currentContact, vcfLines);
    this.changeDetectorRef.markForCheck();
    return currentContact;

  }

  importConversation() {
    this.conversationRepo.importContact(this.contactBlob);
  }

  shouldApplyClassOnResize() {
    let timeEl: any = document.getElementById("time" + this.message.id);
    if (!timeEl) {
      return;
    }
    const timeWidth = document.getElementById("time" + this.message.id)?.clientWidth + 10 || 0;

    const contentWidth = document.getElementById("msg-content" + this.message.id)?.clientWidth || 0;
    if (timeWidth > contentWidth) {
      this.message["flex"] = true;
    } else {
      this.message["flex"] = false;
    }

    this.changeDetectorRef.markForCheck();
  }

  get shouldApplyClass() {
    const timeWidth = document.getElementById("time" + this.message.id)?.clientWidth + 10 || 0;
    const contentWidth = document.getElementById("msg-content" + this.message.id)?.clientWidth || 0;
    if (timeWidth > contentWidth) {
      this.message["flex"] = true;
    }
    this.changeDetectorRef.markForCheck();
    return timeWidth > contentWidth;
  }

  showChatInformation(event, target) {
    let haveContact:boolean = false;
    this.allContacts.map(contact => {
      if (contact.jid === target) {
        haveContact = true;
      }
    });

    if (haveContact) {
      this.conversationRepo.showOnlyProfileTab = false;
    } else {
      this.conversationRepo.showOnlyProfileTab = true;
    }
    const haveProfile = document.getElementsByTagName("vp-chat-right-sidebar");
    if ((haveProfile && haveProfile.length > 0)) {
      this.store.dispatch(new ExpandChatRightSidebar(false));
      return;
    }
    if (environment.showGroupChatInfo && !CommonUtil.isOnMobileDevice() && !this.isSearchResult) {
      event.stopPropagation();
      const animationDiv = document.querySelector(".chat-sidebar-content-block");
      if (animationDiv) {
        const sidebarContentDiv = document.querySelector(".sidebar-content-animation");
        const sidebarHeaderDiv = document.querySelector(".sidebar-header-animation");
        if (sidebarContentDiv){
          sidebarContentDiv["style"].animation = "";
          animationDiv.classList.remove("sidebar-content-animation");
          const headerContainer = document.querySelector(".header-right-container");
          if (headerContainer) {
            const headerRight = headerContainer.querySelector(".header-right");
          }
        }
        if (sidebarHeaderDiv) {
          sidebarHeaderDiv["style"].animation = "";
          animationDiv.classList.remove("sidebar-header-animation");
        }
      }
      let roasterSidebar;
      this.store.select(getIsSideRostersExpanded).pipe(takeUntil(this.isAlive$)).subscribe(expanded => {
        roasterSidebar = expanded;
      });
      if (roasterSidebar) {
        const rosterSidebarEle = document.getElementsByClassName("right-sidebar");
        if (rosterSidebarEle && rosterSidebarEle.length > 0) {
          if (!rosterSidebarEle[0].classList.contains("open-roster-sidebar")) rosterSidebarEle[0].classList.add("open-roster-sidebar");;
        };
      };
      this.broadcaster.broadcast(ConstantsUtil.CLOSE_SIDEBAR);
      this.store.dispatch(new ExpandSideRostersList(false));
      this.store.dispatch(new ExpandChatRightSidebarWithProfile(target));
    }
  }

  sendSignal(issueId?) {
    let signal = {
      target: this.selectedConversation["Target"],
      type: "ticket-preview-update",
      data: String(issueId)
    };
    this.xmpp.sendSignalToTarget(signal);
    if (!CommonUtil.isGroupTarged(this.selectedConversation["Target"])) {
      this.xmpp.sendSignalToMyself(signal);
    }
  }


}

enum ChannelEntityType {
  CHANNEL = "channels",
  TOPIC = "topics",
  COMMENT = "commentId",
  SOCIAL_TOPIC = "socialTopic",
  SOCIAL = "social"
}

enum ChatPreviewType {
  CHANNEL = "CHANNEL",
  TOPIC = "TOPIC",
  COMMENT = "COMMENT",
  SOCIAL = "SOCIAL"
}

enum CommentAction {
  LIKE = "LIKE",
  REPLY = "REPLY",
  VIEW_REPLIES = "VIEW_REPLIES"
}

enum FileActionType {
  COPY_ALL_FILES_TO_CLIPBOARD = "COPY_ALL_FILES_TO_CLIPBOARD",
  DOWNLOAD_ALL_FILES = "DOWNLOAD_ALL_FILES",
  DOWNLOAD_FILE = "DOWNLOAD_FILE",
  COPY_FILE_LINK = "COPY_FILE_LINK",
  COPY_FILE_TO_CLIPBOARD = "COPY_FILE_TO_CLIPBOARD",
  FILE_ITEM_CLICKED = "FILE_ITEM_CLICKED"
}

interface ChatPreviewData {
  previewType: ChatPreviewType;
  resultType: SmartLinkSearchResultType;
  linkText?: string;
  description?: string;
  searchQuery?: string;
  resultData: any;
}
